import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    APP_VERSION,
    CacheService,
    ClockService,
    CountriesService,
    DiscountsAndMultipliersService,
    DocumentIntegrityService,
    DocumentNumberingService,
    SalesDocumentType,
    PersonsService,
    Product,
    ProductsList,
    SalesDocument,
    SalesDocumentPosition,
    SalesDocumentPositionBasePrices,
    SalesDocumentPositionPrices,
    State,
    Synced,
    TaxRatesService,
    DocumentAttachment,
} from '@icc/b2b/shared';
import { core, TranslateService, ConfigService, PricesDataService } from '@icc/shared';
import { ExchangeRateService } from '@icc/shared/currency';
import {
    addAttachment,
    addDocWithPosition,
    addPosition,
    changePosition,
    changePositionPrice,
    detachPositions,
    joinPositions,
    joinDocuments,
    joinAttachments,
    openDoc,
    removeAttachment,
    changePositionBasePrice,
} from './+state/sales-documents.actions';
import { PositionService } from './position.service';
import { filter, first, map, take } from 'rxjs/operators';
import {
    getSalesDocumentPositions,
    getDocumentSynced,
    getSelectedSales,
    getBuyPositionsForPosition,
    getBuyDocumentsForDocument,
    getBuyDocumentAttachmentsByAttachmentUuid,
    getSalesDocumentForBuyDocument,
    getBuyPositionsForDocument,
    getSynced,
    getSalesDocument,
    getSalesDocumentsEntities,
} from './+state/sales-documents.selectors';
import { Subject, combineLatest, firstValueFrom } from 'rxjs';
import {
    PriceVersion,
    DocumentPositionGroup,
    DocumentPositionGroupSchema,
} from '@icc/b2b/shared';
import { Router } from '@angular/router';
import { generateKeyBetween } from 'fractional-indexing';
import { HttpClient } from '@angular/common/http';
import { PriceBaseService2 } from 'libs/price2/src/lib/price-base.service';
import { ClientService } from './client.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { logger, parseDate, ToastService } from '@icc/shared/helpers';
import { AddedProductDialogComponent } from './added-product-dialog/added-product-dialog.component';
import { DialogService } from '@icc/ui';
import { Configuration } from '@icc/configurator/shared';
import { PriceVersionService } from './price-version-selector/price-version.service';
import { OfferValidityService } from './offer-validity/offer-validity.service';
import { CannotAddProductDialogComponent } from './cannot-add-product-dialog/cannot-add-product-dialog.component';
import { SyncService } from './sync.service';
import * as Sentry from '@sentry/browser';
import { DocumentManagerService } from './document-manager.service';
import { DocumentCreationService } from './document-creation.service';
import { DocumentPricesService } from './document-prices.service';
import { B2bDocumentPricesService } from './b2b-document-prices.service';
import { DocumentSlavePositionsService } from './document-slave-positions.service';
import { StatsService } from '@icc/shared/helpers/b2b';
import { B2bDocumentManagerService } from './b2b-document-manager.service';

@Injectable({
    providedIn: 'root',
})
export class SalesDocumentService {
    openedDocument$ = this.store.select(getSelectedSales);
    selectedDocument$ = new Subject<SalesDocument>();
    deletedDocuments$ = new Subject<string[]>();
    positionToScroll: SalesDocumentPosition['uuid'] | null = null;

    private openedDocument: SalesDocument | null = null;
    private selectedDocument: SalesDocument | null = null;
    private deletedDocuments: string[] = [];

    constructor(
        private store: Store<State>,
        private configService: ConfigService,
        private positionService: PositionService,
        private priceVersionService: PriceVersionService,
        private translateService: TranslateService,
        private priceBaseService: PriceBaseService2,
        private router: Router,
        private http: HttpClient,
        private exchangeRateService: ExchangeRateService,
        private taxRatesService: TaxRatesService,
        private permissionsService: NgxPermissionsService,
        private toastService: ToastService,
        private discountsAndMultipliersService: DiscountsAndMultipliersService,
        private dialogService: DialogService,
        private offerValidityService: OfferValidityService,
        private countriesService: CountriesService,
        private personsService: PersonsService,
        private documentIntegrityService: DocumentIntegrityService,
        private syncService: SyncService,
        private documentManagerService: DocumentManagerService,
        private b2bDocumentManagerService: B2bDocumentManagerService,
        private documentCreationService: DocumentCreationService,
        private documentPricesService: DocumentPricesService,
        private b2bDocumentPricesService: B2bDocumentPricesService,
        private documentSlavePositionsService: DocumentSlavePositionsService,
        @Inject(APP_VERSION) private appVersion: string,
        private statsService: StatsService,
        private priceDataService: PricesDataService,
    ) {
        this.openedDocument$.pipe().subscribe((document) => {
            this.openedDocument = document;
            if (document) {
                Sentry.setContext('document', {
                    uuid: document.uuid,
                    documentNumber: document.documentNumber?.join(''),
                    salesDocumentTypeId: document.salesDocumentTypeId,
                    sellerCompanyId: document.sellerCompanyId,
                    buyerCompanyId: document.buyerCompanyId,
                    sellerUserId: document.sellerUserId,
                    buyerUserId: document.buyerUserId,
                    priceVersionId: document.priceVersionId,
                });
            } else {
                Sentry.setContext('document', null);
            }
        });
    }

    async loadData() {
        if (await this.permissionsService.hasPermission('access-trade')) {
            this.exchangeRateService.loadRates();
            this.taxRatesService.loadRates();
            this.countriesService.loadCountries();
            this.offerValidityService.loadValidityDates();
            this.personsService.load();
        }
    }

    async resetPositionBadUnits(salesDocument) {
        const positions = await this.documentManagerService.getDocumentPosition(salesDocument?.uuid);
        positions.forEach((position) => {
            if (position.isInvalid) {
                this.store.dispatch(
                    changePosition({
                        id: position.uuid,
                        docId: salesDocument.uuid,
                        changes: {
                            isInvalid: false,
                        },
                    })
                );
            }
        });
    }
    createDocument(): SalesDocument {
        const defaultCurrency = this.exchangeRateService.getMainCurrency();
        const exchangeRates = this.exchangeRateService.getRates();
        const { key: documentNumber } = DocumentNumberingService.generate(
            this.configService.user.documentNumberingScheme[
                DocumentNumberingService.getNumberingScheme(null, SalesDocumentType.Quotation)
            ],
            {
                buyerId: 0,
                client: {
                    reference: '',
                },
                sellerUserCode: this.configService.user.user.ActiveCompanyCode ?? '',
                marketCode: this.configService.user.market,
                documentCustomCode: '',
                version: 0,
                userId: this.configService.user.user.id,
            }
        );

        const defaultTaxRate = this.taxRatesService.getDefaultTaxRate();
        const seller = this.personsService.getWorkerByUserId(this.configService.user.user.id);
        seller.company = this.configService.user.company;
        const document: SalesDocument = {
            uuid: core.generateUUID(),
            statusId: null,
            flags: {},
            type: 'sales',
            assignedTo: 'unassigned',
            created: new Date(),
            modified: new Date(),
            version: null,
            lang: this.configService.config.languageCode,
            dimensionUnits: 'international',
            priceVersionId: this.b2bDocumentPricesService.activePriceVersion?.id,
            sellerMarketId: this.configService.user.user.ActiveMarket,
            sellerCompanyId: +this.configService.user.user.ActiveCompany,
            sellerUserId: this.configService.user.user.id,
            sellerResourceId: this.configService.user.user?.ActiveResourceId || null,
            seller: ClientService.convertSellerToDocumentClient(seller),
            priceAdditionalDiscount: this.discountsAndMultipliersService.offerDiscounts.defaultDiscount,
            priceCurrency: defaultCurrency.code,
            exchangeRate: {
                rates: exchangeRates,
            },

            salesDocumentTypeId: SalesDocumentType.Quotation,
            taxRateCalculationMethod: this.taxRatesService.getTaxRateCalculationMethod(),
            defaultTaxRateId: defaultTaxRate?.id,
            defaultTaxRate: defaultTaxRate?.value,
            isOneTaxRateMode: false,
            isCustomTaxRatesMode: false,
            isEmpty: true,

            documentNumber,
            discountType:
                this.configService.user.user.ActiveCompanyTradeSettings?.discountType || 'price',
            ignorePriceVersionValidity: this.ignoredPriceVersionValidity(this.b2bDocumentPricesService.activePriceVersion),
        };
        return document;
    }

    createBuyDocumentFromSalesDocument(
        salesDocument: SalesDocument,
        sellerProductDepartmentId: number | null = null
    ): SalesDocument {
        const defaultCurrency = this.exchangeRateService.getBuyCurrency();
        const defaultTaxRate = this.taxRatesService.getBuyDefaultTaxRate();
        const contractor = this.configService.user.contractor;
        if (contractor) {
            const sellerWorker = this.personsService.getSeller(salesDocument);
            contractor.person = {
                userId: sellerWorker?.person?.user.id,
                firstName: sellerWorker?.person?.first_name,
                lastName: sellerWorker?.person?.last_name,
                email: sellerWorker?.person?.email,
                phoneNumber: sellerWorker?.person?.phoneNumber,
            };
            if (+salesDocument.sellerResourceId !== -1) {
                const resource = (this.personsService.getResources() || []).find(el => +el.id === +salesDocument.sellerResourceId);
                const addresses = resource?.deliveryAddresses || [];
                if (addresses.length) {
                    const deliveryAddress = addresses[0];
                    contractor.delivery = deliveryAddress;
                    contractor.deliveryId = deliveryAddress.id;
                }
            }
        }
        const client = contractor
            ? ClientService.convertToDocumentClient(contractor)
            : null;
        let seller = this.configService.user.accountManagers[0];
        if (!seller) {
            seller = {};
        }
        seller.company = this.configService.user.accountManagerCompany;
        const { key: documentNumber } = DocumentNumberingService.generate(
            this.configService.user.documentNumberingScheme[
                DocumentNumberingService.getNumberingScheme(salesDocument)
            ],
            {
                buyerId: +this.configService.user.user.ActiveCompany,
                client: client || {
                    reference: '',
                },
                sellerUserCode:
                    this.configService.user.user.ActiveContractorAccountManager?.code ?? '',
                marketCode: this.configService.user.market,
                documentCustomCode: '',
                version: 0,
                userId: this.configService.user.user.id,
            }
        );

        const flagsToReset = (
            (this.configService.user.statusSystems ?? {})[salesDocument.salesDocumentTypeId]
                .flags ?? []
        )
            .filter((el) => el.resetAfterQuotationCopying)
            .map((el) => el.code)
            .reduce((obj, el) => {
                obj[el] = false;
                return obj;
            }, {});

        return {
            ...salesDocument,
            created: new Date(),
            modified: new Date(),
            uuid: core.generateUUID(),
            id: null,
            sellerMarketId: this.configService.user.user.ActiveMarket,
            sellerCompanyId: this.configService.user.user.contractorCompanyId,
            sellerUserId:
                this.configService.user.user.ActiveContractorAccountManager?.userId != null ?
                Number(this.configService.user.user.ActiveContractorAccountManager?.userId) :
                null,
            sellerResourceId: Number(this.configService.user.user.ActiveContractorAccountManager?.resourceId ?? -1),
            sellerProductDepartmentId,
            buyerCompanyId: salesDocument.sellerCompanyId,
            buyerResourceId: salesDocument.sellerResourceId,
            buyerUserId: salesDocument.sellerUserId,
            buyerContractorId: this.configService.user.user.ActiveContractorId,
            client,
            seller: ClientService.convertSellerToDocumentClient(seller),
            number: null,
            documentNumber,
            defaultTaxRate: defaultTaxRate?.value,
            defaultTaxRateId: defaultTaxRate?.id,
            priceCurrency: defaultCurrency?.code,
            priceBeforePromotionsValue: this.exchangeRateService.exchange(
                salesDocument.priceBeforePromotionsValue,
                salesDocument.priceCurrency,
                defaultCurrency?.code
            ),
            priceBeforeDiscountsValue: this.exchangeRateService.exchange(
                salesDocument.priceBeforeDiscountsValue,
                salesDocument.priceCurrency,
                defaultCurrency?.code
            ),
            priceAdditionalDiscount: 0,
            priceAfterDiscountsValue: this.exchangeRateService.exchange(
                salesDocument.priceAfterDiscountsValue,
                salesDocument.priceCurrency,
                defaultCurrency?.code
            ),
            hasNonValuatedPositions: false,
            hasOnlyCustomValuatedPositions: false,
            taxRateCalculationMethod: this.taxRatesService?.getBuyTaxRateCalculationMethod(),
            version: null,
            documentName: '',

            isAdvancePaid: false,
            isConfirmed: false,
            isConfirmedOnlySalesPositions: false,
            isDelivered: false,
            isOpened: false,
            isOrderCreated: false,
            isOrderPlaced: false,
            isSentToBuyer: true,
            isCreatedBuyerDocument: true,
            isTotalPaid: false,
            isAcceptedForProcessing: false,
            isInProgress: false,
            isCompleted: false,
            flags: {
                ...salesDocument.flags,
                ...flagsToReset,
            },
            parentId: null,
            rootId: null,
        };
    }

    addDocumentWithPosition(document: SalesDocument, position: SalesDocumentPosition) {
        this.store.dispatch(
            addDocWithPosition({
                doc: document,
                position,
            })
        );
    }

    async savePosition(
        positionId: string,
        documentId: string,
        position: Partial<
            Omit<
                SalesDocumentPosition,
                keyof SalesDocumentPositionPrices | keyof SalesDocumentPositionBasePrices
            >
        >,
        onlySales = false,
    ) {
        this.documentManagerService.savePosition(
            positionId,
            documentId,
            position,
            onlySales
        );
        const changedKeys = Object.keys(position);
        if (
            this.configService?.user?.offerRecalculationMethod === 'wholeOffer' &&
            changedKeys.some((key) => ['quantity', 'priceUnitId', 'configuration'].includes(key))
        ) {
            const document = await this.documentManagerService.getDocument(documentId);
            if (document.type === 'sales') {
                this.documentPricesService.recalculatePosition(document);
            }
        }
    }

    async savePositionPrices(
        positionId: string,
        documentId: string,
        position: SalesDocumentPositionPrices,
        buy = false,
        autoRecalculate = true,
    ) {
        this.documentManagerService.savePositionPrices(
            positionId,
            documentId,
            position,
            buy
        );
        if (this.configService?.user?.offerRecalculationMethod === 'wholeOffer' && autoRecalculate) {
            const document = await this.documentManagerService.getDocument(documentId);
            if (document.type === 'sales') {
                this.documentPricesService.recalculatePosition(document, null, false, buy);
            }
        }
    }

    async copyPositionToOtherDocument(position: SalesDocumentPosition, sourceDocument: SalesDocument, sparePartsMode = false) {
        this.router.navigate(['trade', 'offer', 'list', 'offers', 'selection']);
        this.selectedDocument$.pipe(first()).subscribe(async (selectedDocument) => {
            const salesDocument = selectedDocument;
            let duplicateFromInquiry = false;

            if (selectedDocument && Number(selectedDocument.sellerCompanyId) === Number(this.configService.user.user.ActiveCompany) &&
                Number(sourceDocument.sellerCompanyId) !== Number(this.configService.user.user.ActiveCompany) &&
                sourceDocument.salesDocumentTypeId === SalesDocumentType.Inquiry
            ) {
                const buyDocuments = await this.documentManagerService.getBuyDocuments(salesDocument.uuid);
                duplicateFromInquiry = true;
                if (buyDocuments.length) {
                    selectedDocument = buyDocuments[0];
                } else {
                    selectedDocument = this.createBuyDocumentFromSalesDocument(salesDocument);
                    this.documentManagerService.addDocument(selectedDocument);
                    this.documentManagerService.joinDocuments(salesDocument.uuid, selectedDocument.uuid);
                }
            }

            this.selectedDocument = selectedDocument;
            if (selectedDocument) {
                if (duplicateFromInquiry) {
                    const newPosition = await this.duplicateSimplePosition(selectedDocument, sourceDocument, position);
                    const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(
                        salesDocument
                    );
                    const documentPriceVersion = this.priceVersionService.getPriceVersion(
                        salesDocument.priceVersionId
                    );
                    const salesPosition = await this.positionService.createSalesPositionFromBuyPosition(
                        newPosition,
                        salesDocument,
                        documentPriceVersion,
                        currentOfferValidity
                    );
                    this.documentManagerService.addPosition(salesPosition, salesDocument, true);
                    this.store.dispatch(
                        joinPositions({
                            uuid: core.generateUUID(),
                            buyDocumentId: newPosition.documentId,
                            salesDocumentId: salesDocument.uuid,
                            salesPositionId: salesPosition.uuid,
                            buyPositionId: newPosition.uuid,
                        })
                    );
                } else {
                    await this.duplicatePosition(position, selectedDocument, sourceDocument, sparePartsMode);
                }

                if (position.type === 'static' || position.type === 'additional' || sourceDocument?.salesDocumentTypeId === SalesDocumentType.Inquiry || position.customPriceValue) {
                    this.router.navigate(['trade', 'offer', salesDocument.uuid], {
                        replaceUrl: true,
                    });
                    this.toastService.add({
                        title: this.translateService.instant(
                            'SALES_DOCUMENT|Pozycja została skopiowana'
                        ),
                        body: this.translateService.instant(
                            'SALES_DOCUMENT|Pozycja została skopiowana do oferty {offerKey}',
                            { offerKey: selectedDocument.documentNumber?.join('') }
                        ),
                        severity: 'success',
                    });
                }
            }
        });
    }

    async duplicatePosition(position: SalesDocumentPosition, document: SalesDocument, sourceDocument?: SalesDocument, sparePartsMode = false) {
        if (position.type === 'static' || position.type === 'additional' || sourceDocument?.salesDocumentTypeId === SalesDocumentType.Inquiry || position.customPriceValue) {
            await this.duplicateSimplePosition(document, sourceDocument, position);
        } else {
            const commands = ['configurator', sparePartsMode ? 'copy-spare' : 'copy', position.uuid];
            if (position?.configuration?.productId) {
                commands.push(position.configuration.productId);
            } else {
                commands.push('0');
            }
            commands.push(position.documentId);
            this.router.navigate(commands);
        }
    }

    private async duplicateSimplePosition(document: SalesDocument, sourceDocument: SalesDocument, position: SalesDocumentPosition) {
        let newPosition: SalesDocumentPosition;
        if (sourceDocument && Number(document.sellerCompanyId) === Number(this.configService.user.user.ActiveCompany) &&
            Number(sourceDocument.sellerCompanyId) !== Number(this.configService.user.user.ActiveCompany)) {
            const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(
                document
            );
            const documentPriceVersion = this.priceVersionService.getPriceVersion(
                document.priceVersionId
            );
            newPosition = await this.positionService.createSalesPositionFromBuyPosition(position, document, documentPriceVersion, currentOfferValidity);
            newPosition.customPriceValue = sourceDocument?.salesDocumentTypeId === SalesDocumentType.Inquiry || position.customPriceValue;
        } else {
            newPosition = {
                ...position,
                created: new Date(),
                modified: new Date(),
                rootCreated: position.rootCreated || position.created,
                uuid: core.generateUUID(),
                externalId: null,
                id: null,
                documentId: document.uuid,
                priceCurrency: document.priceCurrency,
                priceBeforePromotionsValue: this.exchangeRateService.exchange(
                    position.priceBeforePromotionsValue,
                    position.priceCurrency,
                    document.priceCurrency
                ),
                priceBeforeDiscountsValue: this.exchangeRateService.exchange(
                    position.priceBeforeDiscountsValue,
                    position.priceCurrency,
                    document.priceCurrency
                ),
                priceAfterDiscountsValue: this.exchangeRateService.exchange(
                    position.priceAfterDiscountsValue,
                    position.priceCurrency,
                    document.priceCurrency
                ),
                customPriceValue: sourceDocument?.salesDocumentTypeId === SalesDocumentType.Inquiry || position.customPriceValue,
                masters: {},
                slaves: {},
                pricePromotions: sourceDocument && sourceDocument?.uuid !== document?.uuid ? [] : core.copy(position.pricePromotions),
                priceDiscounts: sourceDocument && sourceDocument?.uuid !== document?.uuid ? [] : core.copy(position.priceDiscounts),
            };

            if (sourceDocument && sourceDocument?.uuid !== document?.uuid) {
                await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
                    document,
                    newPosition,
                    'sales'
                );
            }
        }
        this.store.dispatch(
            addPosition({
                position: newPosition,
            })
        );

        if (this.configService?.user?.offerRecalculationMethod === 'wholeOffer') {
            this.documentPricesService.recalculatePosition(document);
        }
        this.selectedDocument$.next(null);
        return newPosition;
    }

    changeUnitInPosition(position: SalesDocumentPosition, salesDocument: SalesDocument, newUnit) {
        this.store.dispatch(
            changePosition({
                id: position?.uuid,
                docId: salesDocument?.uuid,
                changes: {
                    priceUnitId: +newUnit?.id,
                    configuration: {
                        ...position?.configuration,
                        unitId: newUnit?.id,
                        unitSymbol: newUnit?.symbol,
                        unit: {
                            id: newUnit?.id,
                            symbol: newUnit?.symbol,
                            code: newUnit?.code,
                            factorDesc: newUnit?.factor + ' /' + newUnit?.symbol,
                            name: newUnit?.name,
                        }
                    },
                    isInvalid: false,
                    quantity: position?.quantity || 1,
                },
            })
        );
        this.documentPricesService.recalculatePosition(salesDocument, [position?.uuid], false, false, true);
    }

    getOpenedDocument() {
        return this.openedDocument;
    }

    getSelectedDocument() {
        return this.selectedDocument;
    }

    getDocumentPositions$(documentId: string) {
        return this.store.select(getSalesDocumentPositions(documentId));
    }

    async getSalesDocumentForBuyDocument(documentId: string) {
        const document = await this.syncService.getOrLoadDocument(documentId, getSalesDocument);
        if (document.sellerCompanyId === +this.configService.user.user.ActiveCompany) {
            return null;
        }
        return this.syncService.getOrLoadDocument(documentId, getSalesDocumentForBuyDocument);
    }

    getBuyPositions(positionId: string) {
        return this.store.select(getBuyPositionsForPosition(positionId)).pipe(first()).toPromise();
    }

    getBuyDocumentAttachmentsByAttachmentUuid(attachmentId: string) {
        return this.store
            .select(getBuyDocumentAttachmentsByAttachmentUuid(attachmentId))
            .pipe(first())
            .toPromise();
    }

    async addPositionToActiveDocumentFromProduct(
        product: Product,
        price,
        unitPrice,
        quantity = 1,
        unit?: {
            id: number;
            code: string;
            symbol: string;
        }
    ) {
        const { activeDocument, canAdd } = await this.getActiveDocument('add', product.type === 'addon');
        if (activeDocument && canAdd) {
            const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(
                activeDocument
            );
            const documentPriceVersion = this.priceVersionService.getPriceVersion(
                activeDocument.priceVersionId
            );

            const position = await this.positionService.createPositionFromProduct(
                activeDocument,
                activeDocument.priceCurrency,
                product,
                {
                    price: {
                        value: unitPrice,
                        currency: price.currency,
                        isGross: price.isGross,
                        taxRate: price.taxRate,
                        resetTaxRate: price.resetTaxRate,
                        unitId: unit?.id,
                        unitCode: unit?.code,
                        revision: price.revision || 0,
                    },
                    quantity,
                    unit,
                },
                documentPriceVersion,
                currentOfferValidity,
                await this.documentManagerService.getDocumentPosition(activeDocument.uuid) as SalesDocumentPosition[],
            );
            if (
                activeDocument.sellerProductDepartmentId &&
                activeDocument.sellerProductDepartmentId !== position.department?.id &&
                this.configService.user.user.ActiveCompanyLicenseType === 'license'
            ) {
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Błąd dodawania do oferty'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Nie można dodać produktu do tej oferty - oferta jest przypisana do innego działu.'
                    ),
                    severity: 'error',
                });
                return;
            }

            this.documentManagerService.addPosition(position, activeDocument);
            this.positionToScroll = position.uuid;

            const updatedPositions = await this.documentManagerService.getDocumentPosition(activeDocument.uuid) as SalesDocumentPosition[];
            const positionsToMove = await this.positionService.updateStaticPositionsDepartments(updatedPositions);

            for (const { position: posToMove, newDepartment } of positionsToMove) {
                await this.movePositionToDepartment(posToMove, newDepartment, activeDocument);
            }

            if (this.configService?.user?.offerRecalculationMethod === 'wholeOffer') {
                this.documentPricesService.recalculatePosition(activeDocument);
            }
            if (
                !activeDocument.sellerProductDepartmentId &&
                this.configService.user.user.ActiveCompanyLicenseType === 'license'
            ) {
                activeDocument.sellerProductDepartmentId = position.department?.id;
                this.documentManagerService.saveDocument(activeDocument.uuid, {
                    sellerProductDepartmentId: position.department?.id,
                });
            }

            this.statsService.add({
                action: 'added-product-to-document',
            });

            if (product.type !== 'addon') {
                const unitInfo = this.priceDataService.getUnitInfo(unit?.id, unit?.code);
                this.dialogService.open(AddedProductDialogComponent, {
                    data: {
                        document: activeDocument,
                        product,
                        quantity,
                        price: {
                            ...price,
                            value: unitPrice,
                        },
                        unit: {
                            ...unitInfo,
                            factorDesc: unitInfo?.factorDesc,
                        }
                    },
                });
            }
        }
    }

    async addPositionToActiveDocumentFromConfiguration(
        configuration: Configuration,
        editedPositionId?: string,
        action: 'copy' | 'copy-spare' | 'edit' | 'add' = 'add',
        title: string[] = [],
        lang: string = ''
    ) {
        const { activeDocument, canAdd } = await this.getActiveDocument(action);
        if (activeDocument && canAdd) {
            const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(
                activeDocument
            );
            const documentPriceVersion = this.priceVersionService.getPriceVersion(
                activeDocument.priceVersionId
            );
            if (editedPositionId && action === 'edit') {
                this.positionToScroll = editedPositionId;
                const priceCalculationDate = new Date();
                const positions = await this.documentManagerService.getDocumentPosition(activeDocument.uuid) as SalesDocumentPosition[];
                const editedPosition = positions.find((p) => p.uuid === editedPositionId) as SalesDocumentPosition;
                const position = await this.positionService.updatePositionFromConfiguration(
                    activeDocument,
                    editedPosition,
                    configuration,
                    title,
                    lang
                );
                this.documentManagerService.savePosition(editedPositionId, activeDocument.uuid, {
                    configuration: position.configuration,
                    name: position.name,
                    quantity: position?.quantity || 1,
                    modified: new Date(),
                    title: position.title,
                });
                this.store.dispatch(
                    changePositionBasePrice({
                        id: position.uuid,
                        docId: activeDocument.uuid,
                        changes: {
                            baseBuyPriceTaxRateId: position.baseBuyPriceTaxRateId,
                            baseBuyPriceTaxRate: position.baseBuyPriceTaxRate,
                            baseBuyPriceIsGross: position.baseBuyPriceIsGross,
                            baseBuyPriceValue: position.baseBuyPriceValue ?? undefined,
                            basePriceTaxRateId: position.basePriceTaxRateId,
                            basePriceTaxRate: position.basePriceTaxRate,
                            basePriceIsGross: position.basePriceIsGross,
                            basePriceValue: position.basePriceValue,
                            pricesRevisions: {
                                ...position.pricesRevisions,
                            }
                        },
                    })
                );
                this.savePositionPrices(editedPositionId, activeDocument.uuid, {
                    priceCalculationDate,
                    priceIsGross: position.priceIsGross,
                    priceTaxRateId: position.priceTaxRateId,
                    priceTaxRate: position.priceTaxRate,
                    priceBeforePromotionsValue: position.priceBeforePromotionsValue,
                    pricePromotions: position.pricePromotions,
                    priceBeforeDiscountsValue: position.priceBeforeDiscountsValue,
                    priceAfterDiscountsValue: position.priceAfterDiscountsValue,
                    priceCurrency: activeDocument.priceCurrency,
                    priceDiscounts: position.priceDiscounts,
                    validityDate: this.positionService.getPositionValidity(
                        priceCalculationDate,
                        activeDocument.isConfirmed,
                        documentPriceVersion,
                        currentOfferValidity
                    ),
                    pricesRevisions: {
                        ...position.pricesRevisions,
                    },
                }, false, false);

                await this.documentSlavePositionsService.updateSlavePositions(position, activeDocument, documentPriceVersion, currentOfferValidity, positions);

                if (this.configService?.user?.offerRecalculationMethod === 'wholeOffer' || position.configuration?.priceTaxRate === 0 && position.configuration?.priceIsGross && position.basePriceTaxRateId && position.priceTaxRate !== 0) {
                    if (position.configuration?.priceTaxRate === 0 && position.configuration?.priceIsGross && position.basePriceTaxRateId && position.priceTaxRate !== 0) {
                        Sentry.captureMessage('Recalculating because of 0 tax rate and gross price in configuration', 'error');
                    }
                    this.documentPricesService.recalculatePosition(activeDocument, [], false, false, position.configuration?.priceTaxRate === 0 && position.configuration?.priceIsGross && position.basePriceTaxRateId > 0 && position.priceTaxRate !== 0);
                }
                this.statsService.add({
                    action: 'saved-configuration-to-document',
                });
            } else {
                const positions = await this.documentManagerService.getDocumentPosition(activeDocument.uuid) as SalesDocumentPosition[];
                const editedPosition = editedPositionId && positions.find((p) => p.uuid === editedPositionId);
                const position = await this.positionService.createPositionFromConfiguration(
                    activeDocument,
                    activeDocument.priceCurrency,
                    configuration,
                    title,
                    lang,
                    documentPriceVersion,
                    currentOfferValidity,
                    positions,
                    editedPosition,
                );
                if (
                    activeDocument.sellerProductDepartmentId &&
                    activeDocument.sellerProductDepartmentId !== position.department?.id &&
                    this.configService.user.user.ActiveCompanyLicenseType === 'license'
                ) {
                    this.toastService.add({
                        title: this.translateService.instant(
                            'SALES_DOCUMENT|Błąd dodawania do oferty'
                        ),
                        body: this.translateService.instant(
                            'SALES_DOCUMENT|Nie można dodać produktu do tej oferty - oferta jest przypisana do innego działu.'
                        ),
                        severity: 'error',
                    });
                    return;
                }

                this.documentManagerService.addPosition(position, activeDocument);
                this.positionToScroll = position.uuid;

                const updatedPositions = await this.documentManagerService.getDocumentPosition(activeDocument.uuid) as SalesDocumentPosition[];

                const positionsToMove = await this.positionService.updateStaticPositionsDepartments(updatedPositions);

                for (const { position: posToMove, newDepartment } of positionsToMove) {
                    await this.movePositionToDepartment(posToMove, newDepartment, activeDocument);
                }

                await this.documentSlavePositionsService.updateSlavePositions(position, activeDocument, documentPriceVersion, currentOfferValidity, updatedPositions);

                if (this.configService?.user?.offerRecalculationMethod === 'wholeOffer' || position.configuration?.priceTaxRate === 0 && position.configuration?.priceIsGross && position.basePriceTaxRateId && position.priceTaxRate !== 0) {
                    if (position.configuration?.priceTaxRate === 0 && position.configuration?.priceIsGross && position.basePriceTaxRateId && position.priceTaxRate !== 0) {
                        Sentry.captureMessage('Recalculating because of 0 tax rate and gross price in configuration', 'error');
                    }
                    this.documentPricesService.recalculatePosition(activeDocument, [], false, false, position.configuration?.priceTaxRate === 0 && position.configuration?.priceIsGross && position.basePriceTaxRateId > 0 && position.priceTaxRate !== 0);
                }
                if (
                    !activeDocument.sellerProductDepartmentId &&
                    this.configService.user.user.ActiveCompanyLicenseType === 'license'
                ) {
                    activeDocument.sellerProductDepartmentId = position.department?.id;
                    this.documentManagerService.saveDocument(activeDocument.uuid, {
                        sellerProductDepartmentId: position.department?.id,
                    });
                }
                this.selectedDocument$.next(null);
                this.selectedDocument = null;

                this.statsService.add({
                    action: 'added-configuration-to-document',
                });
            }
        } else {
            if (canAdd) {
                if (activeDocument?.salesDocumentTypeId === SalesDocumentType.Order) {
                    this.toastService.add({
                        title: this.translateService.instant(
                            'SALES_DOCUMENT|Błąd dodawania do zamówienia'
                        ),
                        body: this.translateService.instant(
                            'SALES_DOCUMENT|Nie można dodać produktu do tego zamówienia - zamówienie jest zablokowane do edycji.'
                        ),
                        severity: 'error',
                    });
                } else {
                    this.toastService.add({
                        title: this.translateService.instant('SALES_DOCUMENT|Błąd dodawania do oferty'),
                        body: this.translateService.instant(
                            'SALES_DOCUMENT|Nie można dodać produktu do tej oferty - oferta jest zablokowana do edycji.'
                        ),
                        severity: 'error',
                    });
                }
            }
        }
        return activeDocument;
    }

    createAndSaveDocument() {
        const activeDocument = this.createDocument();
        this.documentManagerService.addDocument(activeDocument);
        return activeDocument;
    }

    createAndSavePairOfDocuments() {
        const activeDocument = this.createDocument();
        this.documentManagerService.addDocument(activeDocument);
        const buyDocument = this.createBuyDocumentFromSalesDocument(activeDocument);
        this.documentManagerService.addDocument(buyDocument);
        this.documentManagerService.joinDocuments(activeDocument.uuid, buyDocument.uuid);
        return activeDocument;
    }

    createAndSaveServiceOrder() {
        const activeDocument = this.createServiceOrder();
        this.documentManagerService.addDocument(activeDocument);
        return activeDocument;
    }

    createServiceOrder(): SalesDocument {
        const defaultCurrency = this.exchangeRateService.getMainCurrency();
        const { key: documentNumber } = DocumentNumberingService.generate(
            this.configService.user.documentNumberingScheme[
                DocumentNumberingService.getNumberingScheme(null, SalesDocumentType.ServiceOrder)
            ],
            {
                buyerId: 0,
                client: {
                    reference: '',
                },
                sellerUserCode: this.configService.user.user.ActiveCompanyCode ?? '',
                marketCode: this.configService.user.market,
                documentCustomCode: '',
                version: 0,
                userId: this.configService.user.user.id,
            }
        );

        const document: SalesDocument = {
            uuid: core.generateUUID(),
            statusId: null,
            flags: {},
            type: 'sales',
            assignedTo: 'unassigned',
            created: new Date(),
            modified: new Date(),
            version: '',
            lang: this.configService.config.languageCode,
            dimensionUnits: 'international',
            priceVersionId: this.b2bDocumentPricesService.activePriceVersion?.id,
            priceAdditionalDiscount: this.discountsAndMultipliersService.offerDiscounts.defaultDiscount,
            priceCurrency: defaultCurrency.code,

            salesDocumentTypeId: SalesDocumentType.ServiceOrder,
            taxRateCalculationMethod: this.taxRatesService.getTaxRateCalculationMethod(),
            isOneTaxRateMode: false,
            isCustomTaxRatesMode: false,
            isEmpty: true,

            documentNumber,
            discountType:
                this.configService.user.user.ActiveCompanyTradeSettings?.discountType || 'price',
            ignorePriceVersionValidity: true,
        };

        if (this.configService.user.user.ActiveCompanyLicenseType === 'license') {
            const seller = this.personsService.getWorkerByUserId(this.configService.user.user.id);
            seller.company = this.configService.user.company;
            document.sellerMarketId = this.configService.user.user.ActiveMarket;
            document.sellerCompanyId = +this.configService.user.user.ActiveCompany;
            document.sellerUserId = this.configService.user.user.id;
            document.sellerResourceId = this.configService.user.user?.ActiveResourceId || null;
            document.seller = ClientService.convertSellerToDocumentClient(seller);
        } else {
            let seller = this.configService.user.accountManagers[0];
            if (!seller) {
                seller = {};
            }
            seller.company = this.configService.user.accountManagerCompany;
            document.buyerCompanyId = +this.configService.user.user.ActiveCompany;
            document.buyerResourceId = this.configService.user.user?.ActiveResourceId || null;
            document.buyerUserId = this.configService.user.user.id;
            document.buyerContractorId = this.configService.user.user.ActiveContractorId;
            document.client = ClientService.convertToDocumentClient(this.configService.user.contractor);

            document.sellerMarketId = this.configService.user.user.ActiveMarket;
            document.sellerCompanyId = +this.configService.user.user.contractorCompanyId;
            document.sellerUserId =
                this.configService.user.user.ActiveContractorAccountManager?.userId != null ?
                Number(this.configService.user.user.ActiveContractorAccountManager?.userId) :
                null;
            document.sellerResourceId = Number(this.configService.user.user.ActiveContractorAccountManager?.resourceId ?? -1);
            document.seller = ClientService.convertSellerToDocumentClient(seller);
        }

        return document;
    }

    validateFile(file: File): boolean {
        const result =
            file?.type &&
            ['application/pdf', 'image/jpeg', 'image/png', 'application/msword'].includes(
                file.type
            );

        if (!result) {
            const fileName = file?.name ? '( ' + file?.name + ' )' : '';
            this.toastService.add({
                title: this.translateService.instant('INTERFACE|Walidacja {fileName}', {
                    fileName,
                }),
                body: this.translateService.instant(
                    'INTERFACE|Akceptowane pliki to pdf, doc, docx, jpg i png.'
                ),
                severity: 'error',
            });
        }
        return result;
    }

    async addAttachmentFromFile(file: File, salesDocumentId: string, positionId?: string) {
        const uuid = core.generateUUID();
        const data = await core.blobToArrayBuffer(file);
        const newAttachment: DocumentAttachment = {
            uuid,
            type: 'sales',
            documentId: salesDocumentId,
            documentPositionId: positionId,
            fileType: file.type,
            created: new Date(),
            modified: new Date(),
            machineId: ClockService.getClock().timestamp.node(),
            name: file.name,
            data,
        };
        let counter = 0;
        CacheService.saveFile(uuid, file);
        counter++;
        this.store.dispatch(
            addAttachment({
                attachment: newAttachment,
            })
        );
        const buyDocuments = await this.documentManagerService.getBuyDocuments(salesDocumentId);
        const buyDocumentsIds = new Set(buyDocuments.map((doc) => doc.uuid));
        const buyPositions = positionId ? await this.getBuyPositions(positionId) : [];

        for (const buyDocumentId of buyDocumentsIds) {
            if (positionId) {
                for (const buyPosition of buyPositions) {
                    if (buyPosition?.uuid && buyPosition.documentId === buyDocumentId) {
                        const buyAttachmentId = core.generateUUID();
                        counter++;
                        this.store.dispatch(
                            addAttachment({
                                attachment: {
                                    ...newAttachment,
                                    uuid: buyAttachmentId,
                                    documentId: buyDocumentId,
                                    documentPositionId: buyPosition.uuid,
                                },
                            })
                        );
                        CacheService.saveFile(buyAttachmentId, file);
                        this.store.dispatch(
                            joinAttachments({
                                uuid: core.generateUUID(),
                                salesDocumentId,
                                buyDocumentId,
                                salesAttachmentId: uuid,
                                buyAttachmentId,
                            })
                        );
                    }
                }
            } else {
                const buyAttachmentId = core.generateUUID();
                counter++;
                this.store.dispatch(
                    addAttachment({
                        attachment: {
                            ...newAttachment,
                            uuid: buyAttachmentId,
                            documentId: buyDocumentId,
                            documentPositionId: null,
                        },
                    })
                );
                CacheService.saveFile(buyAttachmentId, file);
                this.store.dispatch(
                    joinAttachments({
                        uuid: core.generateUUID(),
                        salesDocumentId,
                        buyDocumentId,
                        salesAttachmentId: uuid,
                        buyAttachmentId,
                    })
                );
            }
        }

        if (counter > 3) {
            Sentry.captureMessage('Too many attachments added at once', {
                level: 'warning',
                extra: {
                    attachmentsCount: counter,
                    salesDocumentId,
                    positionId,
                    buyDocumentsIds: Array.from(buyDocumentsIds),
                    file: file.name,
                },
            });
        }
    }

    async removeAttachment(attachmentId: string, documentId: string) {
        this.store.dispatch(
            removeAttachment({
                id: attachmentId,
                docId: documentId,
                isDeleted: true,
            })
        );

        const attachments = await this.getBuyDocumentAttachmentsByAttachmentUuid(attachmentId);
        for (const attachment of attachments) {
            this.store.dispatch(
                removeAttachment({
                    id: attachment.uuid,
                    docId: attachment.documentId,
                    isDeleted: true,
                })
            );
        }
    }

    async confirm(salesDocument: SalesDocument) {
        salesDocument.allowedEditBy = null;
        this.documentManagerService.saveDocument(salesDocument.uuid, {
            isConfirmed: true,
            allowedEditBy: null,
            confirmerUserId: this.configService.user.user.id,
        });
    }

    async confirmOrder(salesDocument: SalesDocument, deliveryDate: string = null, confirmLaterSales = false, confirmLaterBuy = false, placeBuyOrder = false) {
        const parentDocument = await this.documentManagerService.getDocument(salesDocument.parentId) as SalesDocument;
        salesDocument.allowedEditBy = null;
        this.documentManagerService.saveDocument(
            salesDocument.uuid,
            {
                isConfirmed: true,
                allowedEditBy: null,
            },
            salesDocument.isConfirmedOnlySalesPositions && !placeBuyOrder
        );
        let toPlace: SalesDocument[] = [];
        const buyDocuments = await this.documentManagerService.getBuyDocuments(salesDocument.uuid);

        if (parentDocument && parentDocument.type === 'sales') {
            if (!confirmLaterSales) {
                if (parentDocument.isOrderPlaced) {
                    toPlace = [salesDocument];
                }
            }

            if (!confirmLaterBuy && (!salesDocument.isConfirmedOnlySalesPositions || placeBuyOrder)) {
                const toPlaceBuyDocuments = [];
                for (const buyDocument of buyDocuments) {
                    const parentBuyDocument = await this.documentManagerService.getDocument(buyDocument.parentId);
                    if (parentBuyDocument?.type === 'sales' && parentBuyDocument?.isOrderPlaced) {
                        toPlaceBuyDocuments.push(buyDocument);
                    }
                }
                if (toPlaceBuyDocuments.length) {
                    toPlace = toPlaceBuyDocuments;
                }
            }
        }
        if (salesDocument.isConfirmedOnlySalesPositions) {
            this.documentManagerService.saveDocument(
                salesDocument.uuid,
                {
                    isConfirmedOnlySalesPositions: false,
                },
                true
            );
        }
        if (confirmLaterSales) {
            const email: SalesDocument['email'] = {
                uuid: core.generateUUID(),
                type: 'confirmOrderChanges',
                documentIdToSendEmail: salesDocument.uuid,
                neededDocumentsIds: {
                    [salesDocument.uuid]: false,
                },
            };
            this.documentManagerService.saveDocument(salesDocument.uuid, {
                email,
            },
            true);
        } else if (parentDocument?.isOrderPlaced && !salesDocument.isOrderPlaced && buyDocuments.length) {
            salesDocument.isOrderPlaced = true;
            this.documentManagerService.saveDocument(salesDocument.uuid, {
                isOrderPlaced: true,
            }, true);
        }
        if (parentDocument && (!salesDocument.isConfirmedOnlySalesPositions || placeBuyOrder) && (buyDocuments.length ? !confirmLaterBuy : !confirmLaterSales)) {
            const result = await this.placeOrders(toPlace, deliveryDate, salesDocument.uuid);
            return this.afterOrderPlaced(result, toPlace, salesDocument.uuid);
        } else {
            return {
                success: true,
                orders: [],
                documentIdToSendEmail: null,
            };
        }
    }

    cancel(salesDocument: SalesDocument, buyDocuments: SalesDocument[], sendEmailToEndCustomer: boolean) {
        this.documentManagerService.saveDocument(salesDocument.uuid, {
            isCanceled: true,
        });

        if (salesDocument.salesDocumentTypeId === SalesDocumentType.Order) {
            const validBuyDocs = buyDocuments.filter(d => !d.isEmpty);
            const email: SalesDocument['email'] = {
                uuid: core.generateUUID(),
                type: 'cancelOrder',
                documentIdToSendEmail: salesDocument.uuid,
                buyDocumentIdToSendEmail: validBuyDocs[0]?.uuid,
                buyDocumentIdsToSendEmail: validBuyDocs.map(d => d.uuid),
                neededDocumentsIds: {
                    [salesDocument.uuid]: false,
                },
                sendEmailToEndCustomer,
            };
            this.documentManagerService.saveDocument(salesDocument.uuid, {email}, true);
        }
    }

    delete(salesDocument: SalesDocument) {
        if (!salesDocument.isDeleted) {
            salesDocument.isDeleted = true;
            this.documentManagerService.saveDocument(salesDocument.uuid, {
                isDeleted: true,
            });
            this.store.dispatch(
                openDoc({
                    id: null,
                })
            );
            this.addDeletedDocument(salesDocument.uuid);
        }
    }

    addDeletedDocument(documentId: string) {
        this.deletedDocuments.push(documentId);
        this.deletedDocuments$.next(this.deletedDocuments);
    }

    removeDeletedDocument(documentId: string) {
        this.deletedDocuments = this.deletedDocuments.filter((id) => id !== documentId);
        this.deletedDocuments$.next(this.deletedDocuments);
    }

    async deleteWithPositions(salesDocument: SalesDocument) {
        const positions = await this.documentManagerService.getDocumentPosition(salesDocument?.uuid);
        positions.forEach((position) => {
            this.documentManagerService.savePosition(position.uuid, salesDocument.uuid, {
                isDeleted: true,
            });
        });
        const attachments = await this.documentManagerService.getDocumentAttachments(salesDocument?.uuid);
        attachments.forEach((attachment) => {
            this.removeAttachment(attachment.uuid, salesDocument.uuid);
        });
        this.delete(salesDocument);
    }

    async deleteDocumentsRemotely(documents: SalesDocument[]): Promise<Record<string, {
        success: boolean;
    }>> {
        try {
            const result = await firstValueFrom(this.http.post<Record<string, {
                success: boolean;
            }>>('/api/b2b/delete-documents', {
                ids: documents.map(doc => doc.uuid),
                userId: this.configService.user.user.id,
                appVersion: this.appVersion,
            }));
            for (const document of documents) {
                document.isDeleted = true;
                this.addDeletedDocument(document.uuid);
            }
            await firstValueFrom(
                this.store.select(getSalesDocumentsEntities).pipe(
                    map(salesDocuments => {
                        let allDeleted = true;
                        for (const id of documents.map(doc => doc.uuid)) {
                            if (salesDocuments[id] && !salesDocuments[id].isDeleted) {
                                allDeleted = false;
                            }
                        }
                        return allDeleted;
                    }),
                    filter((allDeleted) => allDeleted === true),
                ),
            );
            return result;
        } catch (error) {
            logger.error('Deleting documents failed', error);
            const results = documents.reduce((acc, el) => {
                acc[el.uuid] = {
                    success: false,
                };
                return acc;
            }, {});
            return results;
        }
    }


    async createOrder(salesDocument: SalesDocument, orderLater: boolean, deliveryDate: string = null) {
        salesDocument.isOrderCreated = true;
        salesDocument.isOrderPlaced = true;

        this.documentManagerService.saveDocument(salesDocument.uuid, {
            isOrderCreated: true,
            isOrderPlaced: true,
        });

        if (deliveryDate) {
            salesDocument.declaredDeadline = parseDate(deliveryDate);
            this.documentManagerService.saveDocument(salesDocument.uuid, {
                declaredDeadline: salesDocument.declaredDeadline,
            });
        }

        const duplicatedDocuments = await this.documentCreationService.duplicateSalesAndBuyDocuments(
            salesDocument,
            false,
            true,
            SalesDocumentType.Order,
            !orderLater,
        );

        return duplicatedDocuments;
    }

    async withdrawPlaceOrders(orders: SalesDocument[], documentIdToSendEmail?: string) {
        await firstValueFrom(
            this.syncService.synced$.pipe(
                filter(({ synced }) =>
                    orders
                        .map((doc) => synced.get(doc.uuid) === Synced.Synced)
                        .every((el) => el === true)
                ),
                take(1),
            )
        );
        try {
            const result = await firstValueFrom(this.http.post<{
                success: boolean;
            }>('/api/b2b/withdraw-place-orders', {
                orderIds: orders.map((order) => order.uuid),
                userId: this.configService.user.user.id,
                appVersion: this.appVersion,
                documentIdToSendEmail,
            }));
            await firstValueFrom(
                combineLatest([
                    this.store.select(getSalesDocumentsEntities).pipe(
                        map(documents => {
                            let withdrawn = true;
                            for (const order of orders) {
                                withdrawn = withdrawn && documents[order.uuid].toSendToExternalSystem;
                            }
                            return withdrawn;
                        }),
                        filter((withdrawn) => withdrawn === true),
                    ),
                    this.syncService.synced$.pipe(
                        map(({ synced }) =>
                            orders
                                .map((doc) => synced.get(doc.uuid) === Synced.Synced)
                                .every((el) => el === true)
                        ),
                        filter((synced) => synced === true),
                    )
                ]).pipe(
                    filter(([withdrawn, synced]) => withdrawn && synced),
                )
            );
            return result;
        } catch (error) {
            logger.error('Withdraw Place order failed', error);
            return null;
        }
    }

    async placeOrders(orders: SalesDocument[], deliveryDate: string = null, documentIdToSendEmail?: string) {
        await firstValueFrom(
            this.syncService.synced$.pipe(
                filter(({ synced }) =>
                    orders
                        .map((doc) => synced.get(doc.uuid) === Synced.Synced)
                        .every((el) => el === true)
                ),
                take(1),
            )
        );
        try {
            const result = await firstValueFrom(this.http.post<Record<string, {
                success: boolean;
                documentId: string;
                messages: string[];
                hasIntegration?: boolean;
                error?: string;
                inactiveProducts?: string[];
                message?: string;
            }>>('/api/b2b/place-orders', {
                orderIds: orders.map((order) => order.uuid),
                deliveryDate,
                userId: this.configService.user.user.id,
                appVersion: this.appVersion,
                documentIdToSendEmail,
            }));
            await firstValueFrom(
                combineLatest([
                    this.store.select(getSalesDocumentsEntities).pipe(
                        map(documents => {
                            let allPlaced = true;
                            for (const order of orders) {
                                if (result[order.uuid].success && documents[order.uuid].type === 'sales') {
                                    allPlaced = allPlaced && (documents[order.uuid] as SalesDocument).isOrderPlaced;
                                }
                            }
                            return allPlaced;
                        }),
                        filter((allPlaced) => allPlaced === true),
                    ),
                    this.syncService.synced$.pipe(
                        map(({ synced }) =>
                            orders
                                .map((doc) => synced.get(doc.uuid) === Synced.Synced)
                                .every((el) => el === true)
                        ),
                        filter((synced) => synced === true),
                    )
                ]).pipe(
                    filter(([allPlaced, synced]) => allPlaced && synced),
                )
            );
            return result;
        } catch (error) {
            logger.error('Place order failed', error);
            const results = orders.reduce((acc, el) => {
                acc[el.uuid] = {
                    documentId: el.uuid,
                    success: false,
                    hasIntegration: false,
                    messages: [],
                };
                return acc;
            }, {});
            return results;
        }
    }


    async afterOrderPlaced(results: Record<string, {
        success: boolean;
        documentId: string;
        messages: string[];
        hasIntegration?: boolean;
        error?: string;
        inactiveProducts?: string[];
        message?: string;
    }>, orders: SalesDocument[], documentIdToSendEmail?: string) {
        const result = Object.values(results).reduce((acc, el) => {
            acc.success = acc.success && el.success;
            acc.messages.push(...(el.messages || []));
            acc.integration = acc.integration || el.hasIntegration;
            acc.error = acc.error || el.error;
            if (el.inactiveProducts) {
                acc.inactiveProducts.push(...el.inactiveProducts);
            }
            if (el.message) {
                acc.message = el.message;
            }
            return acc;
        }, {
            success: true,
            messages: [],
            integration: false,
            error: null,
            inactiveProducts: [],
            message: null,
        });
        if (result?.success) {
            return {
                success: true,
                orders,
                documentIdToSendEmail,
                messages: result.messages,
                integration: result.integration,
            };
        } else {
            return {
                success: false,
                orders,
                documentIdToSendEmail,
                messages: result.messages,
                integration: result.integration,
                error: result.error,
                inactiveProducts: result.inactiveProducts,
                message: result.message,
            };
        }
    }

    prepareOrderEmail(
        orders: SalesDocument[],
        documentIdToSendEmail: string,
        orderLater: boolean
    ) {
        if (orderLater) {
            const email: SalesDocument['email'] = {
                uuid: core.generateUUID(),
                type: 'toPlaceOrder',
                documentIdToSendEmail,
                neededDocumentsIds: {
                    [documentIdToSendEmail]: false,
                },
            };
            this.documentManagerService.saveDocument(documentIdToSendEmail, {
                email,
            },
            true);
        } else {
            const email: SalesDocument['email'] = {
                uuid: core.generateUUID(),
                type: 'order',
                documentIdToSendEmail,
                neededDocumentsIds: {
                    [documentIdToSendEmail]: false,
                },
            };
            orders.forEach((el) => {
                email.neededDocumentsIds[el.uuid] = false;
            });
            this.documentManagerService.saveDocument(documentIdToSendEmail, {
                email,
            });
        }
    }

    consult(document: SalesDocument) {
        const email: SalesDocument['email'] = {
            uuid: core.generateUUID(),
            type: 'consult',
            documentIdToSendEmail: document.uuid,
            neededDocumentsIds: {
                [document.uuid]: false,
            },
        };
        this.documentManagerService.saveDocument(document.uuid, {
            email,
        }, true);

        this.toastService.add({
            title: this.translateService.instant('SALES_DOCUMENT|Oferta została wysłana do konsultacji'),
            body: this.translateService.instant('SALES_DOCUMENT|Przesłano prośbę o konsultację oferty nr {offerKey}.',
                {
                    offerKey: document.documentNumber.join(''),
                }
            ),
        });
    }

    async setDocumentCustomCode(salesDocument: SalesDocument, customCode: string) {
        const { key: documentNumber } = DocumentNumberingService.generate(
            this.configService.user.documentNumberingScheme[
                DocumentNumberingService.getNumberingScheme(salesDocument)
            ],
            {
                buyerId: salesDocument.buyerContractorId,
                client: salesDocument.client || {
                    reference: '',
                },
                sellerUserCode: await this.documentManagerService.getSellerUserCode(salesDocument),
                marketCode: this.configService.user.market,
                documentCustomCode: customCode ?? '',
                number: salesDocument.number != null ? String(salesDocument.number) : '',
                version: Number(salesDocument.version),
                userId: this.configService.user.user.id,
                quotationNumber: salesDocument.documentNumber
                    ? salesDocument.documentNumber.join('')
                    : '',
                quotationVersion: Number(salesDocument.version || 0),
                rootDocumentCreationDate: salesDocument.rootCreated,
                creationDate: salesDocument.created,
            }
        );
        this.documentManagerService.saveDocument(
            salesDocument.uuid,
            {
                documentCustomCode: customCode,
                documentNumber,
            },
            true
        );
    }

    changeAdditionalDiscount(salesDocument: SalesDocument, discount: number) {
        this.documentManagerService.saveDocument(
            salesDocument.uuid,
            {
                priceAdditionalDiscount: discount,
            },
            true
        );
    }

    async changePositionPrice(
        position: SalesDocumentPosition,
        salesDocument: SalesDocument,
        price: number
    ) {
        const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(
            salesDocument
        );
        const documentPriceVersion = this.priceVersionService.getPriceVersion(
            salesDocument.priceVersionId
        );
        const priceCalculationDate = new Date();
        position.customPriceValue = position.customPriceValue || price !== position?.priceBeforeDiscountsValue;
        if (position.customPriceValue) {
            position.priceBeforePromotionsValue = price;
            position.priceBeforeDiscountsValue = price;
            position.priceAfterDiscountsValue = price;
            position.baseBuyPriceValue = price;
            position.baseBuyPriceCurrency = position.priceCurrency;
            position.baseBuyPriceIsGross = position.priceIsGross;
            position.baseBuyPriceTaxRate = position.priceTaxRate;
            position.baseBuyPriceTaxRateId = position.priceTaxRateId;
            position.basePriceValue = price;
            position.basePriceCurrency = position.priceCurrency;
            position.basePriceIsGross = position.priceIsGross;
            position.basePriceTaxRate = position.priceTaxRate;
            position.basePriceTaxRateId = position.priceTaxRateId;
            this.discountsAndMultipliersService.recalculateDiscounts(position, salesDocument);
            this.store.dispatch(
                changePositionBasePrice({
                    id: position.uuid,
                    docId: salesDocument.uuid,
                    changes: {
                        baseBuyPriceValue: position.baseBuyPriceValue,
                        baseBuyPriceCurrency: position.baseBuyPriceCurrency,
                        baseBuyPriceIsGross: position.baseBuyPriceIsGross,
                        baseBuyPriceTaxRate: position.baseBuyPriceTaxRate ?? null,
                        baseBuyPriceTaxRateId: position.baseBuyPriceTaxRateId ?? null,
                        basePriceValue: position.basePriceValue,
                        basePriceCurrency: position.priceCurrency,
                        basePriceIsGross: position.priceIsGross ?? false,
                        basePriceTaxRate: position.priceTaxRate ?? null,
                        basePriceTaxRateId: position.priceTaxRateId ?? null,
                    },
                }),
            );
            this.savePosition(position.uuid, salesDocument.uuid, {
                customPriceValue: position.customPriceValue,
            });
            this.savePositionPrices(position.uuid, salesDocument.uuid, {
                priceIsGross: position.priceIsGross,
                priceBeforePromotionsValue: position.priceBeforePromotionsValue,
                pricePromotions: position.pricePromotions,
                priceBeforeDiscountsValue: position.priceBeforeDiscountsValue,
                priceDiscounts: position.priceDiscounts,
                priceAfterDiscountsValue: position.priceAfterDiscountsValue,
                priceCurrency: position.priceCurrency,
                priceCalculationDate,
                validityDate: this.positionService.getPositionValidity(
                    priceCalculationDate,
                    salesDocument.isConfirmed,
                    documentPriceVersion,
                    currentOfferValidity
                ),
            });
        }
    }

    async valuatePositionPrice(position: SalesDocumentPosition, salesDocument: SalesDocument, selectedValidityDate?: Date) {
        const priceCalculationDate = new Date();
        const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(
            salesDocument
        );
        const documentPriceVersion = this.priceVersionService.getPriceVersion(
            salesDocument.priceVersionId
        );
        const validityDate = selectedValidityDate ?? this.positionService.getPositionValidity(
            priceCalculationDate,
            salesDocument.isConfirmed,
            documentPriceVersion,
            currentOfferValidity
        );
        const defaultTaxRate = this.taxRatesService.getDefaultTaxRate();
        const defaultBuyTaxRate = this.taxRatesService.getBuyDefaultTaxRate();

        position.configuration.price = position.configuration.Price;
        position.configuration.priceNoMargin = position.configuration.PriceNoMargin || position.configuration.Price;
        position.configuration.priceAfterMultiplier = position.configuration.PriceAfterMultiplier || position.configuration.Price;
        position.configuration.priceAfterMultiplierNoMargin = position.configuration.PriceAfterMultiplierNoMargin || position.configuration.PriceNoMargin;
        position.configuration.priceAfterDiscounts = position.configuration.PriceAfterDiscounts || position.configuration.Price;
        position.configuration.priceIsGross = position.configuration.PriceIsGross || false;
        position.configuration.priceTaxRate = position.configuration.PriceTaxRate || 0;
        if (position.configuration.Quantity) {
            position.configuration.quantity = position.configuration.Quantity;
        }
        position.configuration.priceSegments = position.configuration.PriceSegments;
        position.configuration.priceSegmentsNoMargin = position.configuration.PriceSegmentsNoMargin;
        position.configuration.priceParts = position.configuration.PriceParts;
        position.configuration.pricePartsNoMargin = position.configuration.PricePartsNoMargin;
        if (position.configuration.DiscountGroups) {
            position.configuration.discountGroups = position.configuration.DiscountGroups;
        }
        if (position.configuration.DiscountGroupsNoMargin) {
            position.configuration.discountGroupsNoMargin = position.configuration.DiscountGroupsNoMargin;
        }
        const configurationTaxRate = this.taxRatesService.matchTaxRate(
            position.configuration.priceTaxRate
        );
        const changedTaxRate = configurationTaxRate?.value !== position.configuration.priceTaxRate;
        position.baseBuyPriceTaxRate = defaultBuyTaxRate?.value;
        position.baseBuyPriceValue = TaxRatesService.recalcPrice(
            position.configuration.PriceIsGross,
            position.configuration.PriceNoMargin,
            position.configuration.PriceIsGross,
            position.configuration.priceTaxRate,
            defaultBuyTaxRate?.value
        );
        position.baseBuyPriceCurrency = position.priceCurrency;
        position.baseBuyPriceTaxRateId = defaultBuyTaxRate.id;
        position.baseBuyPriceIsGross = position.configuration.PriceIsGross;
        position.basePriceValue = position.configuration.Price;
        position.basePriceCurrency = position.priceCurrency;
        position.basePriceTaxRate = (changedTaxRate ? configurationTaxRate?.value : position.configuration.PriceTaxRate) ?? defaultTaxRate.value;
        position.basePriceTaxRateId = configurationTaxRate?.id ?? defaultTaxRate.id;
        position.basePriceIsGross = position.configuration.PriceIsGross;
        position.priceBeforePromotionsValue = position.basePriceValue;
        position.priceBeforeDiscountsValue = position.basePriceValue;
        if (!salesDocument.isCustomTaxRatesMode) {
            position.priceTaxRateId = position.basePriceTaxRateId;
            position.priceTaxRate = position.basePriceTaxRate;
        }
        if (salesDocument.salesDocumentTypeId === SalesDocumentType.Quotation) {
            position.customPriceValue = true;
        }
        await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
            salesDocument,
            position,
            'sales'
        );
        this.store.dispatch(
            changePosition({
                id: position.uuid,
                docId: salesDocument.uuid,
                changes: {
                    configuration: position.configuration,
                    customPriceValue: position.customPriceValue,
                },
            })
        );
        this.store.dispatch(
            changePositionBasePrice({
                id: position.uuid,
                docId: salesDocument.uuid,
                changes: {
                    baseBuyPriceTaxRateId: position.baseBuyPriceTaxRateId,
                    baseBuyPriceTaxRate: position.baseBuyPriceTaxRate,
                    baseBuyPriceIsGross: position.baseBuyPriceIsGross,
                    baseBuyPriceValue: position.baseBuyPriceValue ?? undefined,
                    basePriceTaxRateId: position.basePriceTaxRateId,
                    basePriceTaxRate: position.basePriceTaxRate,
                    basePriceIsGross: position.basePriceIsGross,
                    basePriceValue: position.basePriceValue,
                },
            })
        );
        this.store.dispatch(
            changePositionPrice({
                id: position.uuid,
                docId: salesDocument.uuid,
                changes: {
                    priceTaxRateId: position.priceTaxRateId,
                    priceTaxRate: position.priceTaxRate,
                    priceBeforePromotionsValue: position.priceBeforePromotionsValue,
                    pricePromotions: position.pricePromotions,
                    priceBeforeDiscountsValue: position.priceBeforeDiscountsValue,
                    priceAfterDiscountsValue: position.priceAfterDiscountsValue,
                    priceDiscounts: position.priceDiscounts,
                    priceCurrency: position.priceCurrency,
                    priceIsGross: position.priceIsGross,
                    priceCalculationDate,
                    validityDate,
                },
            })
        );
    }

    async changeCurrency(salesDocument: SalesDocument, code: string, buy = false) {
        const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(
            salesDocument
        );
        const documentPriceVersion = this.priceVersionService.getPriceVersion(
            salesDocument.priceVersionId
        );
        const priceCalculationDate = new Date();
        salesDocument.pricePromotions.forEach((promotion) => {
            promotion.value = this.exchangeRateService.exchange(
                promotion.value,
                salesDocument.priceCurrency,
                code,
                buy
            );
        });
        salesDocument.priceDiscounts.forEach((discount) => {
            discount.value = this.exchangeRateService.exchange(
                discount.value,
                salesDocument.priceCurrency,
                code,
                buy
            );
        });
        this.documentManagerService.saveDocument(
            salesDocument.uuid,
            {
                priceCurrency: code,
                priceBeforePromotionsValue: this.exchangeRateService.exchange(
                    salesDocument.priceBeforePromotionsValue,
                    salesDocument.priceCurrency,
                    code,
                    buy
                ),
                priceBeforeDiscountsValue: this.exchangeRateService.exchange(
                    salesDocument.priceBeforeDiscountsValue,
                    salesDocument.priceCurrency,
                    code,
                    buy
                ),
                priceAfterDiscountsValue: this.exchangeRateService.exchange(
                    salesDocument.priceAfterDiscountsValue,
                    salesDocument.priceCurrency,
                    code,
                    buy
                ),
                priceDiscounts: salesDocument.priceDiscounts,
            },
            true
        );
        const sourceDocumentPositions = await this.documentManagerService.getDocumentPosition(salesDocument.uuid);
        salesDocument.priceCurrency = code;
        for (const position of sourceDocumentPositions as SalesDocumentPosition[]) {
            position.priceCurrency = code;
            const {updatedPositionConfiguration} = await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
                salesDocument,
                position,
                buy ? 'buy' : 'sales'
            );
            this.savePositionPrices(
                position.uuid,
                salesDocument.uuid,
                {
                    priceCurrency: code,
                    priceIsGross: position.priceIsGross,
                    priceTaxRateId: position.priceTaxRateId,
                    priceTaxRate: position.priceTaxRate,
                    priceBeforePromotionsValue: position.priceBeforePromotionsValue,
                    pricePromotions: position.pricePromotions,
                    priceBeforeDiscountsValue: position.priceBeforeDiscountsValue,
                    priceAfterDiscountsValue: position.priceAfterDiscountsValue,
                    priceDiscounts: position.priceDiscounts,
                    priceCalculationDate,
                    validityDate: this.positionService.getPositionValidity(
                        priceCalculationDate,
                        salesDocument.isConfirmed,
                        documentPriceVersion,
                        currentOfferValidity
                    ),
                },
                buy
            );
            if (updatedPositionConfiguration) {
                this.savePosition(position.uuid, salesDocument.uuid, {
                    configuration: position.configuration,
                }, buy);
            }
        }
    }

    async changeBuyCurrency(
        buyDocuments: SalesDocument[],
        code: string,
        salesDocument?: SalesDocument
    ) {
        if (
            !(await this.permissionsService.hasPermission(['trade-change-sale-currency'])) &&
            salesDocument
        ) {
            await this.changeCurrency(salesDocument, code);
        }
        for (const document of buyDocuments) {
            await this.changeCurrency(document, code, true);
        }
    }

    async setOneTaxRateToWholeDocument(salesDocument: SalesDocument, taxRateId: number) {
        const taxRate = this.taxRatesService.getTaxRate(taxRateId);
        const sourceDocumentPositions = await this.documentManagerService.getDocumentPosition(salesDocument.uuid) as SalesDocumentPosition[];
        this.documentManagerService.saveDocument(
            salesDocument.uuid,
            {
                defaultTaxRateId: taxRateId,
                defaultTaxRate: taxRate.value,
            },
            false
        );
        sourceDocumentPositions.forEach((position) => {
            this.changePositionTaxRate(position, salesDocument, taxRateId);
        });
    }

    async changePositionTaxRate(
        position: SalesDocumentPosition,
        salesDocument: SalesDocument,
        taxRateId: number = position.priceTaxRateId
    ) {
        const taxRate = this.taxRatesService.getTaxRate(taxRateId);
        if (taxRate) {
            const {updatedPositionConfiguration} = await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
                salesDocument,
                position,
                'sales',
                taxRate
            );
            this.savePositionPrices(position.uuid, salesDocument.uuid, {
                priceTaxRateId: taxRateId,
                priceTaxRate: taxRate.value,
                priceBeforePromotionsValue: position.priceBeforePromotionsValue,
                pricePromotions: position.pricePromotions,
                priceBeforeDiscountsValue: position.priceBeforeDiscountsValue,
                priceDiscounts: position.priceDiscounts,
                priceAfterDiscountsValue: position.priceAfterDiscountsValue,
                priceIsGross: TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
                priceCurrency: position.priceCurrency,
            });
            if (updatedPositionConfiguration) {
                this.savePosition(position.uuid, salesDocument.uuid, {
                    configuration: position.configuration,
                });
            }
        }
    }

    async changeOneTaxRateMode(salesDocument: SalesDocument, mode: boolean) {
        salesDocument.isOneTaxRateMode = mode;
        this.documentManagerService.saveDocument(
            salesDocument.uuid,
            {
                isOneTaxRateMode: mode,
            },
            true
        );
        if (!mode && this.configService?.user?.offerRecalculationMethod === 'wholeOffer') {
            this.documentPricesService.recalculatePosition(salesDocument);
        }
    }

    async changeCustomTaxRatesMode(salesDocument: SalesDocument, isCustom: boolean) {
        salesDocument.isCustomTaxRatesMode = isCustom;
        this.documentManagerService.saveDocument(
            salesDocument.uuid,
            {
                isCustomTaxRatesMode: isCustom,
            },
            true
        );
    }

    async changeVatCalculationMode(
        salesDocument: SalesDocument,
        method: 'netSummary' | 'netPosition' | 'grossSummary'
    ) {
        salesDocument.taxRateCalculationMethod = method;
        this.documentManagerService.saveDocument(
            salesDocument.uuid,
            {
                taxRateCalculationMethod: method,
            },
            true
        );

        const sourceDocumentPositions = await this.documentManagerService.getDocumentPosition(salesDocument.uuid) as SalesDocumentPosition[];

        sourceDocumentPositions.forEach((position) => {
            this.changePositionTaxRate(position, salesDocument);
        });
    }

    async changePositionDiscount(
        position: SalesDocumentPosition,
        salesDocument: SalesDocument,
        discount,
        type,
        value: number
    ) {
        const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(
            salesDocument
        );
        const documentPriceVersion = this.priceVersionService.getPriceVersion(
            salesDocument.priceVersionId
        );
        const priceCalculationDate = new Date();
        const { updatedConfiguration } = await this.discountsAndMultipliersService.changeDiscount(position, salesDocument, discount, type, value);
        this.savePositionPrices(position.uuid, salesDocument.uuid, {
            priceIsGross: position.priceIsGross,
            priceTaxRate: position.priceTaxRate,
            priceDiscounts: position.priceDiscounts,
            priceAfterDiscountsValue: position.priceAfterDiscountsValue,
            priceCurrency: position.priceCurrency,
            priceCalculationDate,
            validityDate: this.positionService.getPositionValidity(
                priceCalculationDate,
                salesDocument.isConfirmed,
                documentPriceVersion,
                currentOfferValidity
            ),
        });

        if (updatedConfiguration) {
            this.savePosition(position.uuid, salesDocument.uuid, {
                configuration: position.configuration,
            });
        }
    }

    async setPositionOrderInGroup(
        originGroup: DocumentPositionGroup,
        groupingId: DocumentPositionGroupSchema['groupSchemaId'],
        positions: SalesDocumentPosition[],
        oldIndex: number,
        newIndex: number
    ) {
        const { activeDocument } = await this.getActiveDocument();
        if (!activeDocument) {
            return;
        }
        const groups = core.copy(activeDocument.groups);
        const groupingSchema = groups.find((g) => g.groupSchemaId === groupingId);
        const group = groupingSchema.groups?.find((g) => g.uuid === originGroup.uuid);
        const positionId = positions[oldIndex].uuid;
        const prevIndex = oldIndex < newIndex ? newIndex : newIndex - 1;
        const nextIndex = oldIndex < newIndex ? newIndex + 1 : newIndex;

        const positionPrev = positions[prevIndex]?.uuid;
        const positionNext = positions[nextIndex]?.uuid;
        group.positionOrder[positionId] = generateKeyBetween(
            group.positionOrder[positionPrev] ?? null,
            group.positionOrder[positionNext] ?? null
        );
        this.documentManagerService.saveDocument(
            activeDocument.uuid,
            {
                groups,
            },
            true
        );

        const buyDocuments = await this.documentManagerService.getBuyDocuments(activeDocument.uuid);
        for (const buyDocument of buyDocuments) {
            const buyGroups = core.copy(buyDocument.groups);
            const buyGroupingSchema = buyGroups.find((g) => g.groupSchemaId === groupingId);
            const buyGroup = buyGroupingSchema.groups?.find((g) => g.key === group.key);
            const buyPositions = await this.getBuyPositions(positionId);
            const buyPosition = buyPositions.find((el) => el.documentId === buyDocument.uuid);
            if (buyPosition) {
                buyGroup.positionOrder[buyPosition.uuid] = group.positionOrder[positionId];
                this.documentManagerService.saveDocument(
                    buyDocument.uuid,
                    {
                        groups: buyGroups,
                    },
                    true
                );
            }
        }
    }

    async hasAccessToOrder(doc: SalesDocument, action: 'view' | 'manage' | 'cancel' = 'view') {
        let hasAccess = false;
        const documentIsNotInProgress = !doc.isInProgress && !doc.isCompleted && !doc.isDelivered;
        const documentIsUnplaced = !doc.isOrderPlaced && documentIsNotInProgress;
        const documentIsUnaccepted = !doc.isAcceptedForProcessing && documentIsNotInProgress;
        const hasAccessToSellAll =
            Number(this.configService?.user?.user?.ActiveCompany) === Number(doc.sellerCompanyId);
        const hasAccessToSellOwn =
            Number(this.configService?.user?.user?.id) === Number(doc.sellerUserId)
                && Number(this.configService?.user?.user?.ActiveCompany) === Number(doc.sellerCompanyId);
        const hasAccessToSellResourceAll =
            this.configService?.user?.user?.Company?.Resources?.some(el => Number(el.id) === Number(doc.sellerResourceId)) &&
            Number(this.configService?.user?.user?.ActiveCompany) === Number(doc.sellerCompanyId);
        const hasAccessToPurchaseAll =
            Number(this.configService?.user?.user?.ActiveCompany) === Number(doc.buyerCompanyId);
        const hasAccessToPurchaseOwn =
            Number(this.configService?.user?.user?.id) === Number(doc.buyerUserId) &&
            Number(this.configService?.user?.user?.ActiveCompany) === Number(doc.buyerCompanyId);
        const hasAccessToPurchaseResourceAll =
            this.configService?.user?.user?.Company?.Resources?.some(el => Number(el.id) === Number(doc.buyerResourceId)) &&
            Number(this.configService?.user?.user?.ActiveCompany) === Number(doc.buyerCompanyId);
        if (hasAccessToSellAll) {
            if (
                (await this.permissionsService.hasPermission(['sell-all-orders-list'])) &&
                action === 'view'
            ) {
                hasAccess = true;
            }
            if (
                (await this.permissionsService.hasPermission(['sell-all-orders-manage'])) &&
                (action === 'manage' || action === 'cancel')
            ) {
                hasAccess = true;
            }
            if (
                (await this.permissionsService.hasPermission([
                    'sell-all-not-in-progress-orders-manage',
                ])) &&
                (action === 'manage' || action === 'cancel')
            ) {
                hasAccess = hasAccess || documentIsNotInProgress;
            }
        }
        if (hasAccessToSellOwn) {
            if (
                (await this.permissionsService.hasPermission(['sell-own-orders-list'])) &&
                action === 'view'
            ) {
                hasAccess = true;
            }
            if (
                (await this.permissionsService.hasPermission(['sell-own-orders-manage'])) &&
                (action === 'manage' || action === 'cancel')
            ) {
                hasAccess = true;
            }
            if (
                (await this.permissionsService.hasPermission([
                    'sell-own-not-in-progress-orders-manage',
                ])) &&
                (action === 'manage' || action === 'cancel')
            ) {
                hasAccess = hasAccess || documentIsNotInProgress;
            }
        }
        if (hasAccessToSellResourceAll) {
            if (
                (await this.permissionsService.hasPermission(['sell-resource-all-orders-list'])) &&
                action === 'view'
            ) {
                hasAccess = true;
            }
            if (
                (await this.permissionsService.hasPermission(['sell-resource-all-orders-manage'])) &&
                (action === 'manage' || action === 'cancel')
            ) {
                hasAccess = true;
            }
            if (
                (await this.permissionsService.hasPermission([
                    'sell-resource-all-not-in-progress-orders-manage',
                ])) &&
                (action === 'manage' || action === 'cancel')
            ) {
                hasAccess = hasAccess || documentIsNotInProgress;
            }
        }
        if (hasAccessToPurchaseAll) {
            if (
                (await this.permissionsService.hasPermission(['purchase-all-orders-list'])) &&
                action === 'view'
            ) {
                hasAccess = true;
            }
            if (
                (await this.permissionsService.hasPermission([
                    'purchase-all-unplaced-orders-manage',
                ])) &&
                (action === 'manage' || action === 'cancel')
            ) {
                hasAccess = hasAccess || documentIsUnplaced;
            }
            if (
                (await this.permissionsService.hasPermission([
                    'purchase-all-unaccepted-orders-cancel',
                ])) &&
                action === 'cancel'
            ) {
                hasAccess = hasAccess || documentIsUnaccepted;
            }
        }
        if (hasAccessToPurchaseOwn) {
            if (
                (await this.permissionsService.hasPermission(['purchase-own-orders-list'])) &&
                action === 'view'
            ) {
                hasAccess = true;
            }
            if (
                (await this.permissionsService.hasPermission([
                    'purchase-own-unplaced-orders-manage',
                ])) &&
                (action === 'manage' || action === 'cancel')
            ) {
                hasAccess = hasAccess || documentIsUnplaced;
            }
            if (
                (await this.permissionsService.hasPermission([
                    'purchase-own-unaccepted-orders-cancel',
                ])) &&
                action === 'cancel'
            ) {
                hasAccess = hasAccess || documentIsUnaccepted;
            }
        }
        if (hasAccessToPurchaseResourceAll) {
            if (
                (await this.permissionsService.hasPermission(['purchase-resource-all-orders-list'])) &&
                action === 'view'
            ) {
                hasAccess = true;
            }
            if (
                (await this.permissionsService.hasPermission([
                    'purchase-resource-all-unplaced-orders-manage',
                ])) &&
                (action === 'manage' || action === 'cancel')
            ) {
                hasAccess = hasAccess || documentIsUnplaced;
            }
            if (
                (await this.permissionsService.hasPermission([
                    'purchase-resource-all-unaccepted-orders-cancel',
                ])) &&
                action === 'cancel'
            ) {
                hasAccess = hasAccess || documentIsUnaccepted;
            }
        }
        return hasAccess;
    }

    async hasAccessToQuotationOrInquiry(doc: SalesDocument) {
        let hasAccess = false;
        const sell = Number(this.configService?.user?.user?.ActiveCompany) === Number(doc.sellerCompanyId);
        if (sell) {
            if (await this.permissionsService.hasPermission(['sell-all-quotations-list'])) {
                hasAccess =
                    hasAccess ||
                    Number(this.configService?.user?.user?.ActiveCompany) ===
                        Number(doc.sellerCompanyId);
            }
            if (await this.permissionsService.hasPermission(['sell-resource-all-quotations-list'])) {
                hasAccess =
                    hasAccess ||
                    this.configService?.user?.user?.Company?.Resources?.some(el => Number(el.id) ===
                        Number(doc.sellerResourceId)) &&
                        Number(this.configService?.user?.user?.ActiveCompany) ===
                            Number(doc.sellerCompanyId);
            }
            if (await this.permissionsService.hasPermission('sell-own-quotations')) {
                hasAccess =
                    hasAccess ||
                    Number(this.configService?.user?.user?.id) === Number(doc.sellerUserId) &&
                    Number(this.configService?.user?.user?.ActiveCompany) ===
                    Number(doc.sellerCompanyId);
            }
        } else {
            if (await this.permissionsService.hasPermission(['purchase-make-order'])) {
                hasAccess =
                    hasAccess ||
                    Number(this.configService?.user?.user?.ActiveCompany) ===
                        Number(doc.buyerCompanyId);
            }
        }
        return hasAccess;
    }

    async hasAccessToManageQuotation(doc: SalesDocument) {
        let hasAccess = false;
        if (await this.permissionsService.hasPermission(['sell-all-quotations-manage'])) {
            hasAccess =
                hasAccess ||
                Number(this.configService?.user?.user?.ActiveCompany) ===
                    Number(doc.sellerCompanyId);
        }
        if (await this.permissionsService.hasPermission(['sell-resource-all-quotations-manage'])) {
            hasAccess =
                hasAccess ||
                this.configService?.user?.user?.Company?.Resources?.some(el => Number(el.id) ===
                    Number(doc.sellerResourceId)) &&
                    Number(this.configService?.user?.user?.ActiveCompany) ===
                        Number(doc.sellerCompanyId);
        }
        if (await this.permissionsService.hasPermission('sell-own-quotations')) {
            hasAccess =
                hasAccess ||
                Number(this.configService?.user?.user?.id) === Number(doc.sellerUserId) &&
                Number(this.configService?.user?.user?.ActiveCompany) ===
                    Number(doc.sellerCompanyId);
        }
        return hasAccess;
    }

    async hasAccessToManageInquiry(doc: SalesDocument) {
        let hasAccess = false;
        if (await this.permissionsService.hasPermission(['sell-inquiry-manage'])) {
            hasAccess =
                hasAccess ||
                Number(this.configService?.user?.user?.ActiveCompany) ===
                    Number(doc.sellerCompanyId);
        }
        return hasAccess;
    }

    async hasAccessToServiceOrder(doc: SalesDocument) {
        let hasAccess = false;
        if (await this.permissionsService.hasPermission(['all-service-orders-list'])) {
            hasAccess =
                hasAccess ||
                Number(this.configService?.user?.user?.ActiveCompany) ===
                    Number(doc.sellerCompanyId) ||
                Number(this.configService?.user?.user?.ActiveCompany) ===
                    Number(doc.buyerCompanyId);
        }
        return hasAccess;
    }

    async hasAccessToManageServiceOrder(doc: SalesDocument) {
        let hasAccess = false;
        if (await this.permissionsService.hasPermission(['all-service-orders-manage'])) {
            hasAccess =
                hasAccess ||
                Number(this.configService?.user?.user?.ActiveCompany) ===
                    Number(doc.sellerCompanyId) ||
                Number(this.configService?.user?.user?.ActiveCompany) ===
                    Number(doc.buyerCompanyId);
        }
        return hasAccess;
    }


    async hasPermissionSellAllQuotationsList() {
        return await this.permissionsService.hasPermission(['sell-all-quotations-list', 'sell-resource-all-quotations-list']);
    }

    async canAddPositionToQuotation(document: SalesDocument, isOwnProduct = false) {
        let canAdd = false;
        if (document.salesDocumentTypeId === SalesDocumentType.Quotation) {
            canAdd = await this.hasAccessToManageQuotation(document);
        } else if (document.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
            canAdd = await this.hasAccessToManageServiceOrder(document);
        } else {
            canAdd = await this.hasAccessToOrder(document, 'manage');
        }
        if (canAdd) {
            const { invalidOffer } = this.offerValidityService.getValidDate(document);
            canAdd = canAdd &&
                document.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                document?.priceVersionId === this.b2bDocumentPricesService.activePriceVersion?.id &&
                !document.isConfirmed &&
                !document.isCanceled &&
                !document.isDeleted &&
                !document.isOrderPlaced &&
                !invalidOffer &&
                (document.isConfirmedOnlySalesPositions && isOwnProduct ||
                !document.isConfirmedOnlySalesPositions) &&
                (
                    document.salesDocumentTypeId === SalesDocumentType.ServiceOrder && !isOwnProduct ||
                    document.salesDocumentTypeId !== SalesDocumentType.ServiceOrder
                );
        }

        if (canAdd) {
            const positions = await this.documentManagerService.getDocumentPosition(document.uuid);
            const shouldHaveBuyDocument = positions.some(
                (el) => el.type !== 'additional'
            ) && this.configService?.user?.user?.ActiveCompanyLicenseType === 'sublicense'
                && document.salesDocumentTypeId !== SalesDocumentType.ServiceOrder;
            if (shouldHaveBuyDocument) {
                const buyDocuments = await this.documentManagerService.getBuyDocuments(document.uuid);
                canAdd = canAdd && buyDocuments.length > 0;
            }
        }
        return canAdd;
    }

    async canSavePositionToQuotation(document: SalesDocument, isOwnProduct = false) {
        let canAdd = false;

        if (document.salesDocumentTypeId === SalesDocumentType.Quotation) {
            canAdd = await this.hasAccessToManageQuotation(document);
        } else if (document.salesDocumentTypeId === SalesDocumentType.Inquiry) {
            canAdd = await this.hasAccessToManageInquiry(document);
        } else {
            canAdd = await this.hasAccessToOrder(document, 'manage');
        }
        if (canAdd) {
            canAdd = canAdd &&
                document?.priceVersionId === this.b2bDocumentPricesService.activePriceVersion?.id &&
                !document.isConfirmed &&
                !document.isCanceled &&
                !document.isDeleted &&
                !document.isOrderPlaced &&
                (document.isConfirmedOnlySalesPositions && isOwnProduct ||
                !document.isConfirmedOnlySalesPositions);
        }
        return canAdd;
    }

    private async getActiveDocument(action: 'copy' | 'copy-spare' | 'edit' | 'add' = 'add', isOwnProduct = false) {
        let activeDocument: SalesDocument | null = null;
        let canAdd = true;
        if (action === 'copy' || action === 'copy-spare') {
            activeDocument = this.getSelectedDocument();
        }
        if (activeDocument == null) {
            activeDocument = this.getOpenedDocument();
        }
        if (activeDocument == null || (
                action === 'edit'
                ? !(await this.canSavePositionToQuotation(activeDocument, isOwnProduct))
                : !(await this.canAddPositionToQuotation(activeDocument, isOwnProduct))
            )) {
            let addDocument = true;
            if (activeDocument != null) {
                addDocument = (await this.dialogService.open(CannotAddProductDialogComponent, {
                    data: {
                        variant: false,
                    },
                }).afterClosed().toPromise());
            }
            if (addDocument) {
                activeDocument = this.createAndSaveDocument();
                this.store.dispatch(openDoc({ id: activeDocument.uuid }));
                await this.openedDocument$.pipe(first((val) => val != null)).toPromise();
            } else {
                canAdd = false;
            }
        }
        return { activeDocument, canAdd};
    }

    public openDocument(document: SalesDocument) {
        this.store.dispatch(openDoc({ id: document.uuid }));
    }

    public isSynced$() {
        return this.store.select(getSynced);
    }

    public isDocumentSynced$(uuid: string) {
        return this.store.select(getDocumentSynced(uuid));
    }

    public async getWorkersList(): Promise<any> {
        await this.personsService.loaded;
        return this.personsService.getWorkersList();
    }

    async setStatus(salesDocument: SalesDocument, statusId: number) {
        salesDocument.statusId = statusId;
        this.documentManagerService.saveDocument(salesDocument.uuid, {
            statusId,
        }, true);
    }

    async setTitle(salesDocument: SalesDocument, title: string){
        salesDocument.documentName = title;
        this.documentManagerService.saveDocument(salesDocument.uuid, {
            documentName:title,
        }, true);

    }

    async changeDocumentCode(salesDocument: SalesDocument, documentCode: string){
        salesDocument.documentCode = documentCode;
        this.documentManagerService.saveDocument(salesDocument.uuid, {
            documentCode,
        });
    }

    async changeRequestedDeadline(salesDocument: SalesDocument, requestedDeadline: Date){
        salesDocument.requestedDeadline = requestedDeadline;
        this.documentManagerService.saveDocument(salesDocument.uuid, {
            requestedDeadline: salesDocument.requestedDeadline,
        }, true);
    }

    async setFlag(salesDocument: SalesDocument, flag: string, value: boolean, builtin = false) {
        if (builtin) {
            salesDocument[flag] = value;
            this.documentManagerService.saveDocument(salesDocument.uuid, {
                [flag]: value,
            }, true);
        } else {
            if (!salesDocument.flags) {
                salesDocument.flags = {};
            }
            salesDocument.flags[flag] = value;
            this.documentManagerService.saveDocument(salesDocument.uuid, {
                flags: {
                    ...salesDocument.flags,
                    [flag]: value,
                },
            }, true);
        }
    }

    async transferChanges(document: SalesDocument) {
        const buyDocuments = await this.documentManagerService.getBuyDocuments(document.uuid);
        const positions = await this.documentManagerService.getDocumentPosition(document.uuid) as SalesDocumentPosition[];
        const buyPositionsMap = await this.documentManagerService.getBuyPositionsMap(document.uuid);
        const buyPositions: SalesDocumentPosition[] = [];
        if (buyDocuments.length > 0) {
            for (const buyDocument of buyDocuments) {
                buyPositions.push(...(await this.documentManagerService.getDocumentPosition(buyDocument.uuid)) as SalesDocumentPosition[]);
            }
            let newDocument: SalesDocument;
            let salesPositionsUuidMap: Map<string, string>;
            let salesPositionsMap: Map<string, SalesDocumentPosition>;
            let beenDuplicated = false;
            if (!document.isConfirmed) {
                newDocument = document;
                salesPositionsUuidMap = new Map(positions.map((el) => [el.uuid, el.uuid]));
                salesPositionsMap = new Map(positions.map((el) => [el.uuid, el]));
                beenDuplicated = false;
            } else {
                const duplicated = await this.documentCreationService.duplicateSalesOrder(document, document.salesDocumentTypeId === SalesDocumentType.Order);
                newDocument = duplicated.newSalesDocument;
                salesPositionsUuidMap = duplicated.salesPositionsMap;
                salesPositionsMap = new Map(duplicated.salesPositions.map((el) => [el.uuid, el as SalesDocumentPosition]));
                beenDuplicated = duplicated.beenDuplicated;
            }
            const differences = DocumentIntegrityService.checkDocumentsIntegrity(
                document,
                buyDocuments[0]
            );
            if (differences.length > 0) {
                const changes: Partial<SalesDocument> = {};
                for (const diff of differences) {
                    changes[diff.prop] = diff.value2;
                }
                this.documentManagerService.saveDocument(newDocument.uuid, changes, true);
            }
            const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(
                newDocument
            );
            const documentPriceVersion = this.priceVersionService.getPriceVersion(
                newDocument.priceVersionId
            );

            const existingSalesPositionsMap: Map<string, string> = new Map();
            buyPositionsMap.forEach((value, key) => {
                if (value) {
                    existingSalesPositionsMap.set(value.uuid, key);
                }
            });


            for (const buyPosition of buyPositions) {
                if (!existingSalesPositionsMap.has(buyPosition.uuid)) {
                    const salesPosition = await this.positionService.createSalesPositionFromBuyPosition(
                        buyPosition,
                        newDocument,
                        documentPriceVersion,
                        currentOfferValidity
                    );
                    this.documentManagerService.addPosition(salesPosition, newDocument, true);
                    this.store.dispatch(
                        joinPositions({
                            uuid: core.generateUUID(),
                            buyDocumentId: buyPosition.documentId,
                            salesDocumentId: newDocument.uuid,
                            salesPositionId: salesPosition.uuid,
                            buyPositionId: buyPosition.uuid,
                        })
                    );

                    existingSalesPositionsMap.set(buyPosition.uuid, salesPosition.uuid);
                    salesPositionsMap.set(salesPosition.uuid, salesPosition);
                }
            }

            for (const position of buyPositions) {
                if (Object.keys(position.masters || {}).length > 0) {
                    const masters = {};
                    for (const master of Object.keys(position.masters)) {
                        if (existingSalesPositionsMap.get(master)) {
                            masters[existingSalesPositionsMap.get(master)] = position.masters[master];
                        }
                    }
                    salesPositionsMap.get(existingSalesPositionsMap.get(position.uuid)).masters = masters;

                    this.documentManagerService.savePosition(existingSalesPositionsMap.get(position.uuid), newDocument.uuid, {
                        masters,
                    }, true);
                }

                if (Object.keys(position.slaves || {}).length > 0) {
                    const slaves = {};
                    for (const fieldId of Object.keys(position.slaves)) {
                        const slave = position.slaves[fieldId];
                        if (slave && existingSalesPositionsMap.get(slave.positionId)) {
                            slaves[fieldId] = {
                                ...slave,
                                positionId: existingSalesPositionsMap.get(slave.positionId),
                            };
                        }
                    }
                    salesPositionsMap.get(existingSalesPositionsMap.get(position.uuid)).slaves = slaves;

                    this.documentManagerService.savePosition(existingSalesPositionsMap.get(position.uuid), newDocument.uuid, {
                        slaves,
                    }, true)
                }
            }

            for (const position of positions) {
                if (buyPositionsMap.has(position.uuid)) {
                    const positionDifferences = DocumentIntegrityService.checkPositionsIntegrity(
                        position,
                        buyPositionsMap.get(position.uuid),
                        buyPositionsMap,
                    );
                    if (positionDifferences.length > 0) {
                        const changes: Partial<SalesDocumentPosition> = {};
                        const priceChanges: SalesDocumentPositionPrices = {};
                        const priceBaseChanges: SalesDocumentPositionBasePrices = {};
                        const newSalesPosition = salesPositionsMap.get(salesPositionsUuidMap.get(position.uuid));
                        for (const diff of positionDifferences) {
                            if (diff.prop === 'basePriceValue') {
                                const priceCalculationDate = new Date();
                                ([
                                    'basePriceValue',
                                    'basePriceCurrency',
                                    'basePriceIsGross',
                                    'basePriceTaxRate',
                                    'basePriceTaxRateId',
                                ] as const).forEach((key) => {
                                    if (key in buyPositionsMap.get(position.uuid) && buyPositionsMap.get(position.uuid)[key] !== newSalesPosition.basePriceValue) {
                                        priceBaseChanges[key.replace('base', 'baseBuy')] = buyPositionsMap.get(position.uuid)[key];
                                        priceBaseChanges[key as string] = buyPositionsMap.get(position.uuid)[key];

                                        newSalesPosition[key.replace('base', 'baseBuy')] = priceBaseChanges[key.replace('base', 'baseBuy')];
                                        newSalesPosition[key as string] = priceBaseChanges[key as string];
                                    }
                                });

                                const salesBasePriceValue = this.discountsAndMultipliersService.getPositionSalesPrice(
                                    buyPositionsMap.get(position.uuid).basePriceValue,
                                    buyPositionsMap.get(position.uuid)
                                );
                                priceBaseChanges.basePriceValue = salesBasePriceValue;
                                priceBaseChanges.baseBuyPriceValue = buyPositionsMap.get(position.uuid).basePriceValue;
                                newSalesPosition.basePriceValue = salesBasePriceValue;
                                newSalesPosition.baseBuyPriceValue = buyPositionsMap.get(position.uuid).basePriceValue;
                                await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
                                    document,
                                    newSalesPosition,
                                    'sales'
                                );
                                priceChanges.priceBeforePromotionsValue = newSalesPosition.priceBeforePromotionsValue;
                                priceChanges.pricePromotions = newSalesPosition.pricePromotions;
                                priceChanges.priceBeforeDiscountsValue = newSalesPosition.priceBeforeDiscountsValue;
                                priceChanges.priceDiscounts = newSalesPosition.priceDiscounts;
                                priceChanges.priceAfterDiscountsValue = newSalesPosition.priceAfterDiscountsValue;
                                priceChanges.priceCurrency = newSalesPosition.priceCurrency;
                                priceChanges.priceIsGross = newSalesPosition.priceIsGross;
                                priceChanges.priceTaxRate = newSalesPosition.priceTaxRate;
                                priceChanges.priceTaxRateId = newSalesPosition.priceTaxRateId;
                                if (buyPositionsMap.get(position.uuid).customPriceValue) {
                                    priceChanges.priceCalculationDate = buyPositionsMap.get(position.uuid).priceCalculationDate;
                                    priceChanges.validityDate = buyPositionsMap.get(position.uuid).validityDate;
                                } else {
                                    priceChanges.priceCalculationDate = priceCalculationDate,
                                    priceChanges.validityDate = this.positionService.getPositionValidity(
                                        priceCalculationDate,
                                        document.isConfirmed,
                                        documentPriceVersion,
                                        currentOfferValidity
                                    );
                                }
                            } else if (diff.prop === 'configuration') {
                                newSalesPosition[diff.prop] = core.copy(diff.value2);
                                if (newSalesPosition.configuration?.priceSegments) {
                                    const priceSettings = {
                                        taxRate: newSalesPosition.basePriceTaxRate,
                                        isGross: TaxRatesService.isGross(
                                            this.taxRatesService.getTaxRateCalculationMethod()
                                        ),
                                        charge: this.discountsAndMultipliersService.charge,
                                        multiplier: this.discountsAndMultipliersService.getPositionMultiplier(
                                            newSalesPosition
                                        ),
                                        marketFactor: this.discountsAndMultipliersService.marketFactor,
                                        priceVersionId: documentPriceVersion.id,
                                        contractorCompanyId: document.buyerCompanyId,
                                        promotion: this.discountsAndMultipliersService.getPositionPromotion(
                                            newSalesPosition
                                        ),
                                    };
                                    this.priceBaseService.setPreCalculatedPrice(newSalesPosition.configuration, priceSettings);
                                    newSalesPosition.configuration.price = newSalesPosition.configuration.Price;
                                    newSalesPosition.configuration.priceNoMargin = newSalesPosition.configuration.PriceNoMargin || newSalesPosition.configuration.Price;
                                    newSalesPosition.configuration.priceAfterMultiplier = newSalesPosition.configuration.PriceAfterMultiplier || newSalesPosition.configuration.Price;
                                    newSalesPosition.configuration.priceAfterMultiplierNoMargin = newSalesPosition.configuration.PriceAfterMultiplierNoMargin || newSalesPosition.configuration.PriceNoMargin;
                                    newSalesPosition.configuration.priceAfterDiscounts = newSalesPosition.configuration.PriceAfterDiscounts || newSalesPosition.configuration.Price;
                                    newSalesPosition.configuration.priceIsGross = newSalesPosition.configuration.PriceIsGross || false;
                                    newSalesPosition.configuration.priceTaxRate = newSalesPosition.configuration.PriceTaxRate || 0;
                                    if (newSalesPosition.configuration.Quantity) {
                                        newSalesPosition.configuration.quantity = newSalesPosition.configuration.Quantity;
                                    }
                                    newSalesPosition.configuration.priceSegments = newSalesPosition.configuration.PriceSegments;
                                    newSalesPosition.configuration.priceSegmentsNoMargin = newSalesPosition.configuration.PriceSegmentsNoMargin;
                                    newSalesPosition.configuration.priceParts = newSalesPosition.configuration.PriceParts;
                                    newSalesPosition.configuration.pricePartsNoMargin = newSalesPosition.configuration.PricePartsNoMargin;
                                    newSalesPosition.configuration.pricesRevisions = { ...newSalesPosition.configuration.PricesRevisions };

                                    if (newSalesPosition.configuration.PriceSegmentsNoMargin) {
                                        newSalesPosition.configuration.PriceSegments = newSalesPosition.configuration.PriceSegmentsNoMargin;
                                    }
                                    if (newSalesPosition.configuration.PricePartsNoMargin) {
                                        newSalesPosition.configuration.PriceParts = newSalesPosition.configuration.PricePartsNoMargin;
                                    }
                                    if (newSalesPosition.configuration.DiscountGroups) {
                                        newSalesPosition.configuration.discountGroups = newSalesPosition.configuration.DiscountGroups;
                                    }
                                    if (newSalesPosition.configuration.DiscountGroupsNoMargin) {
                                        newSalesPosition.configuration.discountGroupsNoMargin = newSalesPosition.configuration.DiscountGroupsNoMargin;
                                    }
                                    if (newSalesPosition.configuration.PricesRevisions) {
                                        newSalesPosition.configuration.PricesRevisions = { ...newSalesPosition.configuration.PricesRevisions };
                                    }
                                }
                                changes[diff.prop] = newSalesPosition.configuration;
                            } else if (diff.prop === 'pricesRevisions') {
                                if (newSalesPosition.pricesRevisions) {
                                    newSalesPosition.pricesRevisions = {
                                        ...newSalesPosition.pricesRevisions,
                                        ...diff.value2,
                                    };
                                    priceChanges.pricesRevisions = {
                                        ...newSalesPosition.pricesRevisions,
                                    };
                                }
                            } else if (diff.prop === 'masters') {
                                const masters = {};
                                for (const master of Object.keys(diff.value2)) {
                                    if (existingSalesPositionsMap.get(master)) {
                                        masters[existingSalesPositionsMap.get(master)] = diff.value2[master];
                                    }
                                }
                                newSalesPosition.masters = masters;
                            } else if (diff.prop === 'slaves') {
                                const slaves = {};
                                for (const fieldId of Object.keys(diff.value2)) {
                                    const slave = position.slaves[fieldId];
                                    if (slave && existingSalesPositionsMap.get(slave.positionId)) {
                                        slaves[fieldId] = {
                                            ...slave,
                                            positionId: existingSalesPositionsMap.get(slave.positionId),
                                        };
                                    }
                                }
                                newSalesPosition.slaves = slaves;
                            } else if (diff.prop === 'name') {
                                if (typeof diff.value2 === 'object' || !diff.value2) {
                                    newSalesPosition.name = '';
                                } else {
                                    newSalesPosition.name = diff.value2;
                                }
                                changes[diff.prop] = newSalesPosition.name;
                            } else {
                                changes[diff.prop] = core.copy(diff.value2);
                                newSalesPosition[diff.prop] = core.copy(diff.value2);
                            }
                        }
                        this.savePosition(
                            salesPositionsUuidMap.get(position.uuid),
                            newDocument.uuid,
                            changes,
                            true
                        );
                        if (Object.keys(priceBaseChanges).length > 0) {
                            this.store.dispatch(
                                changePositionBasePrice({
                                    id: salesPositionsUuidMap.get(position.uuid),
                                    docId: newDocument.uuid,
                                    changes: priceBaseChanges,
                                    onlySales: true,
                                })
                            );
                        }
                        if (Object.keys(priceChanges).length > 0) {
                            this.savePositionPrices(
                                salesPositionsUuidMap.get(position.uuid),
                                newDocument.uuid,
                                priceChanges,
                                true
                            );
                        }
                    }
                } else if (position.type !== 'additional') {
                    this.savePosition(position.uuid, document.uuid, {
                        isDeleted: true,
                    });
                }
            }

            if (
                this.configService?.user?.offerRecalculationMethod === 'wholeOffer' &&
                newDocument.salesDocumentTypeId !== SalesDocumentType.Order
            ) {
                await this.documentPricesService.recalculatePosition(newDocument);
            }
            if (!beenDuplicated) {
                this.toastService.add({
                    title: this.translateService.instant('DOCUMENT|Modyfikacja dokumentu'),
                    body: this.translateService.instant(
                        'DOCUMENT|Dokument {sourceNumber} został zaktualizowany i jest otwarty do edycji.',
                        {
                            sourceNumber: document.documentNumber?.join(''),
                            targetNumber: newDocument.documentNumber?.join(''),
                        }
                    ),
                    severity: 'info',
                });
            } else {
                this.toastService.add({
                    title: this.translateService.instant('DOCUMENT|Modyfikacja dokumentu'),
                    body: this.translateService.instant(
                        'DOCUMENT|Dokument {sourceNumber} został zduplikowany i zaktualizowany. Nowa wersja dokumentu {targetNumber} jest otwarta do edycji.',
                        {
                            sourceNumber: document.documentNumber?.join(''),
                            targetNumber: newDocument.documentNumber?.join(''),
                        }
                    ),
                    severity: 'info',
                });
            }
            return newDocument;
        }
    }

    async updatePositionsConfigurations(configurations: Record<string, any>) {
        for (const positionId in configurations) {
            if (configurations[positionId]) {
                this.documentManagerService.savePosition(
                    positionId,
                    this.openedDocument?.uuid,
                    {
                        configuration: configurations[positionId],
                    },
                    false
                );
            }
        }

        const documentPriceVersion = this.b2bDocumentPricesService.activePriceVersion;
        const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(this.openedDocument);

        const hasRollerShutterPosition = Object.values(configurations).some(
            config => config?.type === 'roller_shutter'
        );

        const positions = await this.documentManagerService.getDocumentPosition(this.openedDocument.uuid) as SalesDocumentPosition[];

        for (const positionId in configurations) {
            if (configurations[positionId]) {
                const position = positions.find(p => p.uuid === positionId) as SalesDocumentPosition;
                if (position) {
                    if (position.configuration?.type === 'roller_shutter') {
                        continue;
                    }
                    await this.documentSlavePositionsService.updateSlavePositions(
                        position,
                        this.openedDocument,
                        documentPriceVersion,
                        currentOfferValidity,
                        positions
                    );
                }
            }
        }

        if (hasRollerShutterPosition) {
            await this.documentSlavePositionsService.updateAllRollerShutterPackagingSlavePositions(
                this.openedDocument,
                documentPriceVersion,
                currentOfferValidity,
                positions
            );
        }

        this.documentPricesService.recalculatePosition(this.openedDocument, Object.keys(configurations), false, false, true);
    }

    ignoredPriceVersionValidity(priceVersion: PriceVersion) {
        return this.configService.user.user.ActiveCompanyLicenseType === 'license' &&
            priceVersion?.visibleForEmployee &&
            new Date() >= new Date(priceVersion?.expiredDate);
    }


    ignorePriceVersionValidity(document: SalesDocument) {
        this.documentManagerService.saveDocument(document.uuid, {
            ignorePriceVersionValidity: true,
        }, true);
    }

    scrollToPosition(id = this.positionToScroll) {
        setTimeout(() => {
            if (id) {
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
                this.positionToScroll = null;
            }
        }, 0)
    }

    async movePositionToDepartment(
        position: SalesDocumentPosition,
        newDepartment: { id: number | null; name: string },
        salesDocument: SalesDocument
    ) {
        const buyPositions = await firstValueFrom(
            this.store.select(getBuyPositionsForPosition(position.uuid))
        );

        if (buyPositions && buyPositions.length > 0) {
            const buyDocuments = await firstValueFrom(
                this.store.select(getBuyDocumentsForDocument(salesDocument.uuid))
            );

            for (const buyPosition of buyPositions) {
                const oldBuyDocument = buyDocuments.find(doc => doc.uuid === buyPosition.documentId);

                if (!oldBuyDocument) {
                    continue;
                }

                let targetBuyDocument = buyDocuments.find(
                    doc => Number(doc.sellerProductDepartmentId) === Number(newDepartment.id) ||
                        (!doc.sellerProductDepartmentId && !newDepartment.id)
                );

                if (!targetBuyDocument) {
                    targetBuyDocument = this.createBuyDocumentFromSalesDocument(
                        salesDocument,
                        newDepartment.id
                    );
                    this.documentManagerService.addDocument(targetBuyDocument);
                    this.store.dispatch(
                        joinDocuments({
                            uuid: core.generateUUID(),
                            salesDocumentId: salesDocument.uuid,
                            buyDocumentId: targetBuyDocument.uuid,
                        })
                    );
                }

                if (targetBuyDocument.uuid === oldBuyDocument.uuid) {
                    continue;
                }

                this.store.dispatch(
                    detachPositions({
                        salesDocumentId: salesDocument.uuid,
                        buyDocumentId: oldBuyDocument.uuid,
                        salesPositionId: position.uuid,
                        buyPositionId: buyPosition.uuid,
                    })
                );

                this.documentManagerService.savePosition(
                    buyPosition.uuid,
                    oldBuyDocument.uuid,
                    { isDeleted: true },
                    true
                );

                const documentPriceVersion = this.priceVersionService.getPriceVersion(
                    targetBuyDocument.priceVersionId
                );
                const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(
                    targetBuyDocument
                );

                const updatedPosition = { ...position, department: newDepartment };

                const buyPositionsMap = await this.b2bDocumentManagerService.getBuyPositionsMap(salesDocument.uuid);
                const newBuyPosition = await this.positionService.createBuyPositionFromSalesPosition(
                    updatedPosition,
                    targetBuyDocument,
                    documentPriceVersion,
                    currentOfferValidity,
                    buyPositionsMap
                );

                this.store.dispatch(
                    addPosition({
                        position: newBuyPosition,
                        onlySales: true,
                    })
                );

                this.store.dispatch(
                    joinPositions({
                        uuid: core.generateUUID(),
                        salesDocumentId: salesDocument.uuid,
                        salesPositionId: position.uuid,
                        buyDocumentId: newBuyPosition.documentId,
                        buyPositionId: newBuyPosition.uuid,
                    })
                );
            }
        }

        this.documentManagerService.savePosition(
            position.uuid,
            salesDocument.uuid,
            { department: newDepartment },
            false
        );
    }

    async removePosition(position: SalesDocumentPosition, document = this.openedDocument) {
        this.documentManagerService.savePosition(position.uuid, document.uuid, {
            isDeleted: true,
        });
        const documentPriceVersion = this.priceVersionService.getPriceVersion(
            document.priceVersionId
        );
        const currentOfferValidity = this.offerValidityService.getCurrentOfferValidity(document);
        await this.documentSlavePositionsService.updateSlavePositionsOnDelete(
            position,
            document,
            documentPriceVersion,
            currentOfferValidity
        );

        const updatedPositions = await this.documentManagerService.getDocumentPosition(document.uuid) as SalesDocumentPosition[];
        const positionsToMove = await this.positionService.updateStaticPositionsDepartments(updatedPositions);

        for (const { position: posToMove, newDepartment } of positionsToMove) {
            await this.movePositionToDepartment(posToMove, newDepartment, document);
        }

        if (this.configService?.user?.offerRecalculationMethod === 'wholeOffer') {
            this.documentPricesService.recalculatePosition(document);
        }
    }
}
